import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Select, { SingleValue, components } from "react-select";
import { AsyncPaginate, type LoadOptions } from "react-select-async-paginate";
import { Office, Agent, Addresses, Address, Supplier, Product, License, Addresstax } from "types";
import se from "assets/libs/moment/src/locale/se";
import { co } from "@fullcalendar/core/internal-common";
import { e } from "assets/libs/swiper/shared/update-swiper.mjs";
import { StatusBadge } from "components/Productlocationstatus";
import RealtorProductPicker from "components/installationpop";
import PostTypeSignConfiguration from "pages/signage/PostTypeSignConfiguration";
import {
  type LinkedProduct,
  type InstallationOrderLineCustomizations,
} from "components/ProductSelectionModal";
import AddressFormModal from "components/AddAddressFormModal";
import InstallationStatusTracker from "components/InstallationStatusTracker";
import SectionHeader from "components/SectionPillPropsIcons";
import InstructionsCreateCard from "components/Notes";
import ProductSelection from "assets/images/brands/ProductSelection.png"
import Doller from "assets/images/brands/doller.png"
import OrderNotes from "assets/images/brands/OrderNotes.png"
import AddressAutocomplete from "components/AddressAutocomplete";
import { GOOGLE_MAPS_SCRIPT_LIBRARIES } from "constants/googleMapsScript";
import SpecialFeeui from "components/Tax/InstallationandremovalSpecialfeeui";
import PONumber from "components/PONumber";
import { OrderDiscountCodeField } from "components/orderResolve";
import { DiscountAmountDisplay } from "components/DiscountLabelBadge";
import {
  computeInstallationUnitsCostFromChangedUnitPrice,
  getInstallationLineChangedUnitCostTotal,
  getInstallationLineDiscountDisplay,
  getOriginalCatalogUnitPrice,
  getResolveRequestUnitPrice,
  orderLineKey,
} from "utils/orderResolveLinePricing";
import {
  buildInstallationResolvePayload,
  buildInstallationAddResolveLineItems,
  postInstallationOrderResolve,
  applyInstallationResolveResponse,
  overlayInstallationSubmitFromResolved,
  mergeResolvedTaxIntoLineItems,
  getResolvedDiscountDisplay,
  getOrderDiscountFieldDisplay,
  getInstallationResolveRootDiscountCode,
  inferInstallationDiscountScope,
  inferInstallationDiscountScopeFromResolved,
  splitInstallationDiscountForPayload,
  mapInstallationSpecialFeeForResolve,
  mapInstallationSpecialFeeForSubmit,
  mapSavedInstallationSpecialFeeToUi,
  normalizeInstallationSelectedFee,
  type ResolvedInstallationOrder,
  type ResolvedInstallationLineItem,
  type InstallationResolveAddress,
} from "orderResolve";
import { sendInstallationOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { fetchMarketplaceProfilesSelect2 } from "api/postTypeApiCalls";
import { getCustomizationBatch } from "api/realesyStudioApi";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import {
  getCustomizationsBlockFromLineItem,
  getVisibleCustomizationFieldsForDisplay,
  resolveSelectedLineItemForDisplay,
} from "utils/installationProductCustomizationDisplay";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";
import {
  isMongoObjectId,
  normalizeCustomizationBatchContexts,
} from "utils/signageStudioBatchRestore";
import {
  getInstallationOrderFromBatch,
  parseIsoToDateAndTime,
  splitFullName,
  installationAddressDisplayFromSnapshot,
  buildAddressesFromInstallationSnapshot,
  normalizeInstallationPriority,
} from "utils/signageInstallationStudioRestore";
import type { StudioBatchLineItem, StudioCustomizationFlowProps } from "components/ordercheckoutpop";

import { getAuthCookie } from "../../utils/authCookies";
type ProductObj = {
  product_id: string;
  agent_id: string;
  office_id: string;
  inventory_type: string;
  product: Product;
  linked_installer_details: any
  created_by: {
    id: string;
    name: string;
  };
};

type Selected = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}
export type FlattenedProduct = {
  rowKey?: string;
  product_id: string;
  name: string;
  sku: string;
  inventory_type: "agent" | "office" | string;
  product_location_status?: string;
  user_id?: string;
  product_current_status?: string;
  current_status?: string;
  availableqty: number;
  quantity: number;
  updateStatus?: string;
  productionStatus?: string;
  image_url?: string;
  /** True when the product was added as an accessory (quantity editable) */
  is_accessory?: boolean;
  location?: Array<{
    quantity: number;
    warehouse_id: string;
    warehouse_name?: string;
    zone_id?: string;
    zone_name?: string;
    rack_id?: string;
    rack_name?: string;
    bin_id?: string;
    bin_name?: string;
  }>;
  /** New/catalog product: from Browse Catalog; no inventory API call; used for line_items */
  is_new_catalog?: boolean;
  variant_id?: string;
  unit_price?: number;
  is_taxable?: boolean;
  /** Slot key when product was added from a post-type slot; used to clear slot on delete */
  slotKey?: string;
  /** Backend slot id when product was added from a post-type slot; used for line_items + view mode */
  slot_id?: string;
  /** Realesy Studio design metadata when product was added with an active batch line */
  design_id?: string;
  design_name?: string;
  /** Work-drive preview file id */
  preview_url?: string;
  /** Catalog variant customization answers for installation API `line_items[].customizations`. */
  customizations?: InstallationOrderLineCustomizations;
};
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "12px",
  overflow: "hidden",
  // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
  // border: "1px solid #dee2e6",          // Light border
};
const center = {
  lat: 40.712776, // Default latitude (e.g., New York City)
  lng: -74.005974, // Default longitude
};

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
    </components.DropdownIndicator>
  );
};

// Custom styles to ensure dropdown indicator is always visible
const customSelectStyles = {
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    display: 'flex !important',
    opacity: 1,
    visibility: 'visible',
  }),
  indicatorSeparator: (provided: any, state: any) => ({
    ...provided,
    display: 'none',
  }),
};

const AgentAddInstallation = () => {
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const userName = getAuthCookie("userName");
  const licenseType = getAuthCookie("licenseType");
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [quantity, setQuantity] = useState(1);
  const [addressObjects, setAddressObjects] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [productObjs, setProductObjs] = useState<ProductObj[]>([]);
  const [selectedProductOption, setSelectedProductOption] = useState<any | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [installationCost, setInstallationCost] = useState<number | null>(null);
  const [totalUnits, setTotalUnits] = useState<number>(0);
  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
  const [isMarkerDraggable, setIsMarkerDraggable] = useState(false);
  const [userInfo, setUserInfo] = useState<License>();
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const closePicker = () => setPickerOpen(false);
  const [instructions, setInstructions] = useState<string>("");

  // ✅ Simple state to hold selected special fees (bypasses Formik timing issues)
  const [selectedSpecialFees, setSelectedSpecialFees] = useState<any[]>([]);
  const [allAvailableSpecialFees, setAllAvailableSpecialFees] = useState<any[]>([]);
  const [isRecomputingTax, setIsRecomputingTax] = useState(false);
  const [couponInput, setCouponInput] = React.useState("");
  const [couponResolveError, setCouponResolveError] = React.useState("");
  const [appliedVoucher, setAppliedVoucher] = React.useState<{ code: string; amount: number } | null>(null);
  const [manualUnitPriceMap, setManualUnitPriceMap] = useState<Record<string, number>>({});

  const updateProducts = (next: FlattenedProduct[]) => {
    setSelectedProducts(next);
    // onProductsChange?.(next);
  };

  const handleAddFromPicker = (flattened: FlattenedProduct) => {
    // Previous flow blocked adding the same product more than once:
    // const exists = selectedProducts.some((p) => p.product_id === flattened.product_id);
    // if (exists) {
    //   showError?.("Product already added");
    //   return;
    // }
    const nextRow: FlattenedProduct = {
      ...flattened,
      location: flattened.location || [],
      rowKey:
        flattened.rowKey ||
        `${flattened.product_id}-${flattened.variant_id ?? ""}-${flattened.slotKey ?? "manual"}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
    };
    const next = [...selectedProducts, nextRow];
    updateProducts(next);
    setPickerOpen(false);
  };

  const removeRow = (rowKeyOrProdId: string) => {
    const item = selectedProducts.find((p) => (p.rowKey || p.product_id) === rowKeyOrProdId);
    if (item?.slotKey) postTypeConfigRef.current?.clearSlot(item.slotKey);
    updateProducts(selectedProducts.filter((p) => (p.rowKey || p.product_id) !== rowKeyOrProdId));
  };

  const navigate = useNavigate();
  const location = useLocation();
  const safeNavigate = createSafeNavigate(navigate);

  const studioReturnBatchId = useMemo(
    () => new URLSearchParams(location.search).get("batch_id") || "",
    [location.search]
  );
  const studioReturnContextId = useMemo(
    () => new URLSearchParams(location.search).get("context_id") || "",
    [location.search]
  );
  const [studioBatchLineItems, setStudioBatchLineItems] = useState<StudioBatchLineItem[]>([]);
  const isStudioRestoreApplyingRef = React.useRef(false);
  useEffect(() => {
    if (!studioReturnBatchId?.trim()) {
      setStudioBatchLineItems([]);
    }
  }, [studioReturnBatchId]);
  const restoredStudioReturnKeyRef = React.useRef("");
  const buildInstallationOrderPayloadRef = useRef<((values: any) => Promise<Record<string, unknown> | null>) | null>(null);
  const lastResolvedInstallationRef = useRef<ResolvedInstallationOrder | null>(null);
  const recomputeRequestIdRef = React.useRef<number>(0);
  const lastRecomputeParamsRef = React.useRef<string>("");
  const isUpdatingFromRecomputeRef = React.useRef<boolean>(false);
  const [resolveTick, setResolveTick] = React.useState(0);
  const recomputeRef = React.useRef<((options?: { force?: boolean; discountCode?: string; fees?: any[] }) => Promise<boolean>) | null>(null);
  const triggerRecomputeIfReadyRef = React.useRef<() => void>(() => {});
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_SCRIPT_LIBRARIES,
  });

  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  /** Map instance + latest pin so `onLoad` can resize/center without a stale closure. */
  const installationMapRef = useRef<google.maps.Map | null>(null);
  const selectedLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    selectedLocationRef.current = selectedLocation;
  }, [selectedLocation]);

  const [selectedAgent, setSelectedAgent] = useState<Selected | null>(null);

  const [selectedBillingAddress, setSelectedBillingAddress] = useState<Addresses | null>(null);

  const [installationPost, setInstallationPost] = useState<import("pages/signage/PostTypeSignConfiguration").InstallationPostPayload | null>(null);
  const installationPostRef = React.useRef<typeof installationPost>(null);
  installationPostRef.current = installationPost;
  const postTypeConfigRef = React.useRef<import("pages/signage/PostTypeSignConfiguration").PostTypeSignConfigurationRef | null>(null);

  const [installationAddress, setInstallationAddress] = useState<Addresses>();
  const UNIQUE_ID_KEY1 = localStorage.getItem("re_installation_unique_id");
  const [dynamicCostId, setDynamicCostId] = React.useState<string>("");
  const [dynamicTaxable, setDynamicTaxable] = React.useState<boolean>(false);
  // wherever you render cost breakdown:
  // const [couponDiscounts, setCouponDiscounts] = React.useState<Discount[]>([]);
  const [showTaxDetails, setShowTaxDetails] = useState(false);
  const pendingInstructionFilesRef = useRef<{ id: string; file: File }[]>([]);
  const handlePendingInstructionFilesChange = useCallback((items: { id: string; file: File }[]) => {
    pendingInstructionFilesRef.current = items;
  }, []);
  const [selectedInstaller, setSelectedInstaller] = useState<{
    value: string;
    label: string;
  } | null>(null);


  const installerOptions = suppliers?.map((supplier) => ({
    value: supplier.supplier_id,
    label: supplier.name,
  }));

  const productOptions = productObjs?.map((item) => ({
    value: item.product_id,
    label: item.product.name,
  }));
  const pickerUserType: "agent" | "office" | null =
    userInfo?.userType
      ? "agent"
      : "agent";

  const pickerUserId: string =
    userInfo?.id ||
    "";
  const rows = useMemo(
    () =>
      selectedProducts.filter(
        (p) => p.is_new_catalog || (p.inventory_type !== "rotational" && p.inventory_type !== "")
      ),
    [selectedProducts]
  );
  const filtered = (selectedProducts || []).filter(
    (p) => p.is_new_catalog || (p.inventory_type !== "rotational" && p.inventory_type !== "")
  );
  const isPurchasedProductLine = (product: Partial<FlattenedProduct>) => {
    const inventoryType = String(product?.inventory_type || "").toLowerCase();
    return Boolean(product?.is_new_catalog) || inventoryType === "custom";
  };
  const newCatalogUnitsPurchased = useMemo(
    () =>
      (selectedProducts || []).reduce((sum, p) => {
        if (!p?.is_new_catalog) return sum;
        return sum + (Number(p?.quantity) || 0);
      }, 0),
    [selectedProducts]
  );

  const customizationWorkDriveFileIds = useMemo(() => {
    const HEX = /^[a-f0-9]{24}$/i;
    const ids: string[] = [];
    for (const p of selectedProducts || []) {
      const block = getCustomizationsBlockFromLineItem(p);
      const fields = Array.isArray((block as { fields?: unknown[] })?.fields)
        ? ((block as { fields: unknown[] }).fields as Array<{ field_type?: string; value?: unknown }>)
        : [];
      for (const f of fields) {
        const t = String(f?.field_type || "").toLowerCase();
        const v = String(f?.value ?? "").trim();
        if ((t === "file" || t === "image") && v && HEX.test(v)) ids.push(v);
      }
    }
    return Array.from(new Set(ids));
  }, [selectedProducts]);

  const customizationImageUrlMapData = useMemo(
    () =>
      customizationWorkDriveFileIds.length === 0
        ? []
        : [{ product: { variants: [{ images: customizationWorkDriveFileIds.map((url) => ({ url })) }] } }],
    [customizationWorkDriveFileIds]
  );
  const customizationImageUrlMap = useVariantImageUrlMap(customizationImageUrlMapData as any, token);

  /** Logged-in agent from GET /users/:id — drives POST .../customer_side/datatable (user_id + license_type). */
  const postTypeOrderContext = useMemo((): import("pages/signage/PostTypeSignConfiguration").PostTypeOrderEndUserContext | null => {
    if (!userInfo?.id) return null;
    const lt = String(userInfo.licenseType || licenseType || "").trim();
    if (!lt) return null;
    return { userId: String(userInfo.id).trim(), licenseType: lt };
  }, [userInfo?.id, userInfo?.licenseType, licenseType]);

  const colCount = 7; // update if your table has a different number of columns

  // Fetch parent hierarchy for BROKERAGE_AGENT login
  const fetchParentHierarchy = async () => {
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Find the parent with license_type "SIGNAGE_FIRM"
        const signageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "SIGNAGE_FIRM"
        );

        if (signageFirm) {
          setParentSignageId(signageFirm.id || signageFirm._id);
          setParentSignageName(signageFirm.name);
        }
      }
    } catch (error) {
      safeConsole.error("Error fetching parent hierarchy:", error);
      await handleApiError(error, {
        module: "installation",
        action: "Agent_Add_Installation_fetch_parent_hierarchy",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        method: "GET",
      }, token);
    }
  };

  useEffect(() => {
    fetchParentHierarchy();
  }, []);

  const uploadOrderDocumentsToOrder = useCallback(
    async (orderDocumentId: string, files: File[]) => {
      const id = String(orderDocumentId || "").trim();
      if (!token || !id || !files.length) return;

      const fd = new FormData();
      fd.append("id", id);
      fd.append("collection_name", "installation");
      files.forEach((f) => fd.append("files[]", f));

      const res = await fetch(`${process.env.REACT_APP_API_URL}/upload-documents-to-order`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (!res.ok) {
        const raw = await res.json().catch(() => ({}));
        const msg =
          (raw as any)?.message ||
          (raw as any)?.error ||
          `Upload failed (${res.status})`;
        throw new Error(typeof msg === "string" ? msg : "Upload failed");
      }
    },
    [token]
  );

  useEffect(() => {
    const fetchLicense = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch license data");
        }

        const data = await response.json();
        // console.log("Create Signage Data:", data);
        if (data) {
          setUserInfo(data);
        }

      } catch (error) {
        safeConsole.error("Error fetching license data:", error);
        setError("Failed to fetch license data.");
        await handleApiError(error, {
          module: "installation",
          action: "Agent_Add_Installation_fetch_license",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/users/${userId}`,
          method: "GET",
        }, token);
      } finally {
        setLoading(false);
      }
    };

    fetchLicense();
  }, [userId, token]);

  useEffect(() => {
    const fetchSuppliersByUserId = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/agent/suppliers/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch supplier list");
        }

        const data = await response.json();
        console.log("Suppliers:", data);
        setSuppliers(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        await handleApiError(err, {
          module: "installation",
          action: "Agent_Add_Installation_fetch_suppliers_by_user_id",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/agent/suppliers/${userId}`,
          method: "GET",
        }, token);
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliersByUserId();
  }, [token, userId])

  /** Load vendor options from marketplace profiles Select2; value = vendor_id, label = text (vendor name) */
  const loadVendorOptions: LoadOptions<{ value: string; label: string }, any, { page: number }> = useCallback(
    async (search, _, additional) => {
      if (!token) return { options: [], hasMore: false };
      const page = additional?.page ?? 1;
      try {
        const { results, pagination } = await fetchMarketplaceProfilesSelect2(token, {
          search: search || "",
          page,
          limit: 10,
          category: "signage",
        });
        const options = results.map((r) => ({
          value: r.vendor_id ?? r.id,
          label: r.text,
        }));
        return { options, hasMore: pagination?.more ?? false };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [token]
  );

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        console.log("Fetched Addresses:", data);
        setAddressObjects(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        await handleApiError(err, {
          module: "installation",
          action: "Agent_Add_Installation_fetch_addresses",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
          method: "GET",
        }, token);
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [token]);

  const addressOptions =
    addressObjects !== null
      ? addressObjects?.map((address) => ({
        ...address,
        value: address._id,
        label: `${address.label}: ${address?.address_details?.addressLine1}, ${address?.address_details?.city}, ${address?.address_details?.state}, ${address?.address_details?.zip ? address?.address_details?.zip : ''}`,
      }))
      : [];

  // based on instaletion Address we are calling the dyniamc cost
  useEffect(() => {
    const fetchInstallationCost = async () => {
      if (
        !installationAddress?.address_details?.latitude ||
        !installationAddress?.address_details?.longitude ||
        installationAddress?.address_details?.latitude === 0 ||
        installationAddress?.address_details?.longitude === 0
      ) {
        showError(
          "Selected installation address is missing latitude/longitude. Please enter the address manually."
        );
        return;
      }

      const costPayload = {
        installation_address: {
          addressLine1: installationAddress?.address_details?.addressLine1,
          addressLine2: installationAddress?.address_details?.addressLine2,
          city: installationAddress?.address_details?.city,
          state: installationAddress?.address_details?.state,
          country: installationAddress?.address_details?.country,
          zip: installationAddress?.address_details?.zip,
          cityCode: installationAddress?.address_details?.cityCode,
          stateCode: installationAddress?.address_details?.stateCode,
          countryCode: installationAddress?.address_details?.countryCode,
          latitude: installationAddress?.address_details?.latitude,
          longitude: installationAddress?.address_details?.longitude,
        }
      };

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/installation_orders/dynamic_cost/${selectedInstaller?.value}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(costPayload),
          }
        );

        const data = await response.json();
        console.log("Installation cost:", data);
        if (!response.ok) throw new Error(data?.error || "Failed to fetch cost");
        setDynamicCostId(data.id);
        setDynamicTaxable(data.is_taxable || data.taxable);
        setInstallationCost(data.cost || 0); // adjust based on response shape
        triggerRecomputeIfReadyRef.current();
      } catch (error: any) {
        safeConsole.error("Cost fetch error:", error.message);
        setInstallationCost(null);
        await handleApiError(error, {
          module: "installation",
          action: "Agent_Add_Installation_fetch_installation_cost",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/installation_orders/dynamic_cost/${selectedInstaller?.value}`,
          method: "POST",
          requestBody: costPayload,
        }, token);
      }
    };
    if (installationAddress) {
      fetchInstallationCost();
    }
  }, [installationAddress, selectedInstaller]);

  type Status = "on_site" | "with_supplier";

  type LinkedInstallerLocation = {
    quantity: number;
    warehouse_id: string;
    warehouse_name?: string;
    zone_id?: string;
    zone_name?: string;
    rack_id?: string;
    rack_name?: string;
    bin_id?: string;
    bin_name?: string;
  };

  type LinkedInstallerRow = {
    current_status: Status;                 // "on_site" | "with_supplier"
    product_location_status: string;        // mirror to result rows
    quantity?: number;                      // for bucket rows (no per-location)
    location?: LinkedInstallerLocation[];   // for per-location rows
  };

  type LinkedQuantityLocation = {
    quantity: number;
    current_status: Status;                 // EXACTLY "on_site" or "with_supplier"
    product_location_status: string;
    location?: Array<LinkedInstallerLocation & { quantity: number }>;
  };

  const sumAll = (rows: LinkedInstallerRow[]) =>
    rows.reduce((t, r) => {
      if (r.location?.length) {
        return t + r.location.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
      }
      return t + (Number(r.quantity) || 0);
    }, 0);

  function allocateFromLinkedInstallerStrict(
    requestedQty: number,
    liRows: LinkedInstallerRow[],
  ): LinkedQuantityLocation[] {
    if (!Number.isFinite(requestedQty) || requestedQty <= 0) {
      showError(`Invalid requestedQty: ${requestedQty}`);
    }
    if (!Array.isArray(liRows)) {
      showError("linked_installer rows invalid");
    }

    const liTotal = sumAll(liRows);
    if (liTotal < requestedQty) {
      showError(
        `Insufficient linked_installer stock (${liTotal}) for requested quantity (${requestedQty})`
      );
    }

    let remaining = requestedQty;
    const result: LinkedQuantityLocation[] = [];

    // Phase A: ON_SITE buckets (no per-location)
    const rowsOnSiteBuckets = liRows.filter(
      (r) => r.current_status === "on_site" && !(r.location?.length)
    );
    for (const row of rowsOnSiteBuckets) {
      if (remaining === 0) break;
      const take = Math.min(remaining, Number(row.quantity) || 0);
      if (take > 0) {
        result.push({
          quantity: take,
          current_status: "on_site",
          product_location_status: row.product_location_status,
        });
        remaining -= take;
      }
    }

    // Phase B1: WITH_SUPPLIER bucket rows FIRST (wrap into single location[] entry)
    const rowsSupplierBuckets = liRows.filter(
      (r) => r.current_status === "with_supplier" && !(r.location?.length)
    );
    for (const row of rowsSupplierBuckets) {
      if (remaining === 0) break;
      const take = Math.min(remaining, Number(row.quantity) || 0);
      if (take > 0) {
        result.push({
          quantity: take,
          current_status: "with_supplier",
          product_location_status: row.product_location_status,
          location: [
            {
              quantity: take,
              warehouse_id: "",
              warehouse_name: "",
              zone_id: "",
              zone_name: "",
              rack_id: "",
              rack_name: "",
              bin_id: "",
              bin_name: "",
            },
          ],
        });
        remaining -= take;
      }
    }

    // Phase B2: WITH_SUPPLIER per-location rows (combine into one entry per row)
    const rowsSupplierPerLoc = liRows.filter(
      (r) => r.current_status === "with_supplier" && (r.location?.length ?? 0) > 0
    );
    for (const row of rowsSupplierPerLoc) {
      if (remaining === 0) break;

      const locArray: LinkedInstallerLocation[] = [];
      for (const loc of row.location || []) {
        if (remaining === 0) break;
        const take = Math.min(remaining, Number(loc.quantity) || 0);
        if (take > 0) {
          locArray.push({
            quantity: take,
            warehouse_id: loc.warehouse_id,
            warehouse_name: loc.warehouse_name,
            zone_id: loc.zone_id,
            zone_name: loc.zone_name,
            rack_id: loc.rack_id,
            rack_name: loc.rack_name,
            bin_id: loc.bin_id,
            bin_name: loc.bin_name,
          });
          remaining -= take;
        }
      }

      if (locArray.length > 0) {
        result.push({
          quantity: locArray.reduce((sum, l) => sum + l.quantity, 0),
          current_status: "with_supplier",
          product_location_status: row.product_location_status,
          location: locArray,
        });
      }
    }

    if (remaining > 0) {
      showError("insufficient stock after allocation");
    }

    const sum = result.reduce((s, r) => s + r.quantity, 0);
    if (sum !== requestedQty) {
      showError(`Allocation sum (${sum}) != requested quantity (${requestedQty})`);
    }

    return result;
  }


  function getLiRowsFromMatched(matched: any): LinkedInstallerRow[] {
    const rows: any[] =
      matched?.linked_installer_details?.storage_locations ?? [];

    // Normalize to the allocator's expected shape
    return rows.map((r) => ({
      current_status: r.current_status,               // "on_site" | "with_supplier"
      product_location_status: r.product_location_status,
      quantity: r.location?.length ? undefined : Number(r.quantity) || 0,
      location: Array.isArray(r.location) ? r.location.map((loc: any) => ({
        quantity: Number(loc.quantity) || 0,
        warehouse_id: String(loc.warehouse_id || ""),
        warehouse_name: loc.warehouse_name || "",
        zone_id: loc.zone_id || "",
        zone_name: loc.zone_name || "",
        rack_id: loc.rack_id || "",
        rack_name: loc.rack_name || "",
        bin_id: loc.bin_id || "",
        bin_name: loc.bin_name || "",
      })) : [],
    }));
  }
  const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      userType: "agent",
      firstName: userInfo?.firstName || "",
      lastName: userInfo?.lastName || "",
      userEmail: userInfo?.email || "",
      userPhone: userInfo?.phone1 || userInfo?.phone2 || "",
      teamMemberId: "",
      teamMemberName: "",
      installer: "",
      company: "",
      update_status: "",
      product_status: "",
      office: "",
      agent: "",
      scheduled_date: "",
      scheduled_time: "",
      jobs_today: 0,
      installeId: "",
      priority: null,
      billing_address: "",
      installation_address: "",
      isSameAsBilling: false,
      billingAddressId: "",
      installationAddressId: "",
      location: "",
      products: "",
      instructions: "",
      payment_method: "",
      image: null,
      discount: 0,
      subtotal: 0,
      total: 0,
      tax: 0,
      localLawsChecked: false,
      po_number: "",
      gatecode: "",
      label_of_address: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      country: "",
      state: "",
      zipcode: "",
      discount_amount: 0,
      product_discount_amount: 0,
      discount_code: "",
      discount_applies_to: "",
      phone: DEFAULT_TEN_ZEROS || "",
      shippingMethod: "",
      paymentMethod: "",
      latitude: "", // ✅ add this
      longitude: "", // ✅ add this
      installation_cost: 0,
      install_taxable: true,
      install_tax_percentage: null,
      calc_install_rate_str: 0,
      calc_overall_rate_str: 0,
      calc_fee_rate_str: 0,
      destination: { addressLine1: "", city: "", state: "", zip: "", country: "US", stateCode: "", countryCode: "US" },
      specialfee: [] as Array<{
        id: string;
        name: string;
        amount: number;
        taxable?: boolean;
        tax_percentage?: number | null;
      }>,
      totals: {
        installation_subtotal: 0,
        installation_tax_amount: 0,
        product_subtotal: 0,
        product_tax_amount: 0,
        units_cost: 0,
        specialfee_subtotal: 0,
        specialfee_tax_amount: 0,
        grand_total: 0,
        cartId: "",
        specialfee_id: [],
        specialfee_name: [],
      },
      resolved_line_items: [] as ResolvedInstallationLineItem[],
      tax_status: "",
      additional_cost: 0,
      subtotal_amount: 0,
      draft_unique_id: "",
      already_paid: 0,

    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      userEmail: Yup.string()
        .email("Invalid email")
        .required("Email is required"),
      userPhone: Yup.string().required("Phone number is required"),
      installer: Yup.string().required("Installer is required"),
      priority: Yup.string().required("Priority is required"),
      billingAddressId: Yup.string().required("Billing address is required"),

      installationAddressId: Yup.string().when("isSameAsBilling", {
        is: false,   // ✅ explicitly check false
        then: (schema) => schema.required("Installation address is required"),
        otherwise: (schema) => schema.notRequired()
      }),
      isSameAsBilling: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      // ✅ Validate required special fees BEFORE submission
      const requiredFees = allAvailableSpecialFees.filter((f: any) => f.required === true);
      const selectedFeeIds = selectedSpecialFees.map((f: any) => String(f.id));
      const missingRequiredFees = requiredFees.filter((f: any) => !selectedFeeIds.includes(String(f.id || f._id)));

      if (missingRequiredFees.length > 0) {
        const feeNames = missingRequiredFees.map((f: any) => f.name).join(", ");
        showError(`The following required special fees must be selected: ${feeNames}`);
        return; // Don't submit
      }
      const cartId = String(
        values.draft_unique_id ||
          UNIQUE_ID_KEY1 ||
          (typeof localStorage !== "undefined" ? localStorage.getItem("re_installation_unique_id") : "") ||
          ""
      );
      if (!cartId || cartId === "000000000000000000000000") {
        showError("UNIQUE_ID_KEY1 is missing or zeros. Please try again.");
        return;
      }

      // Validate installation post: visible slots must have a product/image or be removed
      const slotErrors = postTypeConfigRef.current?.validateInstallationPost?.() ?? [];
      if (slotErrors.length > 0) {
        showError(slotErrors[0]);
        return;
      }

      setFormSubmitting(true);
      let installationOrderPayload: any;
      try {
        if (!ready) {
          showError("Complete installer and installation address before saving.");
          setFormSubmitting(false);
          return;
        }

        // await recomputeRef.current?.({ force: true });
        // if (!lastResolvedInstallationRef.current) {
        //   showError("Pricing could not be calculated. Please try again.");
        //   setFormSubmitting(false);
        //   return;
        // }

        installationOrderPayload = (await buildInstallationOrderPayloadRef.current?.(values)) as any;
        if (!installationOrderPayload) {
          showError("Unable to build installation order.");
          setFormSubmitting(false);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/installation_orders`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(installationOrderPayload),
        }
        );
        const data = await response.json();
        if (!response.ok) {
          const userEmailError = data?.userEmail;
          const userPhoneError = data?.userPhone;

          if (userEmailError) {
            formik.setFieldError("userEmail", userEmailError);
            formik.setFieldTouched("userEmail", true, false);
          }

          if (userPhoneError) {
            formik.setFieldError("userPhone", userPhoneError);
            formik.setFieldTouched("userPhone", true, false);
          }

          if (userEmailError || userPhoneError) {
            setFormSubmitting(false);
            return;
          }

          showError(data.error || data.message || "Failed to create installation");
          throw new Error(data.message || "Failed to create installation");
        }
        if (data?.order_id) {
          const pendingFiles = pendingInstructionFilesRef.current.map((x) => x.file);
          if (pendingFiles.length > 0) {
            try {
              await uploadOrderDocumentsToOrder(String(data.order_id), pendingFiles);
              pendingInstructionFilesRef.current = [];
            } catch (uploadErr) {
              showError(
                uploadErr instanceof Error
                  ? `Installation created, but attachments failed to upload: ${uploadErr.message}`
                  : "Installation created, but attachments failed to upload."
              );
            }
          }
          showSuccess("Installation order created successfully!");

          // ✅ Trigger 1: Agent Installation Order Created - Send notifications
          try {
            const { createNotification } = await import("../../services/notificationApi");
            const { getAgentInstallationOrderCreatedContent } = await import("../../services/notificationContent");
            const { fetchUserData } = await import("../../utils/userDataFetcher");
            
            const orderNumber = data?.order_number || data.order_id;
            const supplierId = selectedInstaller?.value;
            let supplierName = "Supplier";
            const agentName = `${values.firstName || ""} ${values.lastName || ""}`.trim() || userName || "Agent";
            
            if (supplierId) {
              try {
                const supplierData = await fetchUserData(supplierId, token || "");
                supplierName = supplierData?.companyName || supplierData?.firmDetails?.companyName || supplierName;
              } catch (err) {
                safeConsole.warn("Failed to fetch supplier data:", err);
              }
            }

            // Notify Agent
            if (userId && token) {
              const agentContent = getAgentInstallationOrderCreatedContent({
                orderId: orderNumber,
                supplierName,
                agentName,
                recipientType: "agent",
              });
              await createNotification(userId, agentContent, token, "agent");
            }

            // Notify Selected Installer (Signage Admin/Team Member) only
            if (supplierId && token) {
              try {
                const supplierData = await fetchUserData(supplierId, token || "");
                const supplierLicenseType = supplierData?.licenseType || "SIGNAGE_FIRM";
                const supplierRecipientType = supplierLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";

                const signageContent = getAgentInstallationOrderCreatedContent({
                  orderId: orderNumber,
                  supplierName,
                  agentName,
                  recipientType: supplierRecipientType,
                });
                await createNotification(supplierId, signageContent, token, supplierRecipientType);
              } catch (err) {
                safeConsole.warn(`Failed to send notification to selected installer ${supplierId}:`, err);
              }
            }
          } catch (notifError) {
            safeConsole.warn("Failed to send installation order created notification:", notifError);
            // Don't block the order creation if notification fails
          }

          // Send email notifications for installation order created (centralized)
          try {
            const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
            if (from && data.order_id && token) {
              await sendInstallationOrderTriggerEmail({
                triggerName: "Agent Installation Order Created",
                installationOrderId: String(data.order_id),
                token,
                fromEmail: from,
                includeOrderPdfAttachment: true,
              });
            }
          } catch (emailError) {
            safeConsole.warn("Failed to send installation order created email:", emailError);
          }
        }
        localStorage.removeItem("re_installation_unique_id");
        localStorage.removeItem(UNIQUE_ID_KEY);
        safeNavigate(`/agent/jobs/viewInstallation/${data.order_id}`, { state: { type: "View" } });
      } catch (error: any) {
        safeConsole.error(error);
        showError(`Error: ${error.message}`);
        await handleApiError(error, {
          module: "installation",
          action: "Agent_Add_Installation_create_installation_order",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/installation_orders`,
          method: "POST",
          requestBody: installationOrderPayload,
        }, token);
      } finally {
        setFormSubmitting(false);
        setUploadProgress(0);
      }
    },
  });

  const { values, setFieldValue } = formik;

  /** Signage add-installation uses logged-in user id; agent uses selected vendor. Prefer formik when installer id exists but AsyncPaginate state is out of sync. */
  const pickerSignageCompanyId = String(selectedInstaller?.value ?? values.installer ?? "").trim();
  const canShowAdd = Boolean(pickerSignageCompanyId);

  const formikValuesForStudioSnapshotRef = React.useRef(formik.values);
  formikValuesForStudioSnapshotRef.current = formik.values;

  // Studio return (?batch_id=&context_id=): map batch items for ProductSelectionModal; merge design onto matching product rows.
  React.useEffect(() => {
    const key = `${studioReturnBatchId}|${studioReturnContextId}`;
    if (!studioReturnBatchId || !studioReturnContextId || !token) return;
    // Wait until user profile arrives; otherwise enableReinitialize can wipe restored fields.
    if (!userInfo) return;
    if (restoredStudioReturnKeyRef.current === key) return;

    let cancelled = false;

    (async () => {
      try {
        isStudioRestoreApplyingRef.current = true;
        const batch = await getCustomizationBatch(studioReturnBatchId);
        if (cancelled) return;
        {
          const rawItems = (batch as { items?: unknown[] }).items;
          if (Array.isArray(rawItems)) {
            setStudioBatchLineItems(
              rawItems
                .map((it: unknown) => {
                  const row = it as Record<string, unknown>;
                  const cust = (row.customization ?? {}) as Record<string, unknown>;
                  const pid = String(row.product_id ?? row.line_item_key ?? "").trim();
                  const vid = String(row.variant_id ?? "").trim();
                  return {
                    product_id: pid,
                    variant_id: vid,
                    design_id: String(cust.design_id ?? "").trim() || undefined,
                    preview_id: String(cust.preview_id ?? "").trim() || undefined,
                    design_name: String(cust.design_name ?? "").trim() || undefined,
                    context_id: String(row.active_context_id ?? "").trim() || undefined,
                  } satisfies StudioBatchLineItem;
                })
                .filter((x) => x.product_id && x.variant_id)
            );
          } else {
            setStudioBatchLineItems([]);
          }
        }
        const contexts = normalizeCustomizationBatchContexts(batch);
        const io = getInstallationOrderFromBatch(batch);
        if (contexts.length === 0 && !io) return;

        const findContextForRow = (row: { product_id?: string; variant_id?: string }) => {
          const pid = String(row.product_id ?? "").trim();
          const vid = String(row.variant_id ?? "").trim();
          const samePv = (c: (typeof contexts)[number]) =>
            String(c.product_id ?? "").trim() === pid && String(c.variant_id ?? "").trim() === vid;
          return (
            contexts.find((c) => c.context_id === studioReturnContextId && samePv(c)) ??
            contexts.find(samePv)
          );
        };

        const mergeContextsIntoRows = () => {
          if (contexts.length === 0) return;
          setSelectedProducts((prev) => {
            if (!prev.length) return prev;
            let changed = false;
            const updated = prev.map((row) => {
              const ctx = findContextForRow(row);
              if (!ctx || !ctx.design_id) return row;
              changed = true;
              const pv = String(ctx.preview_id ?? "").trim();
              const usePreviewForThumb = pv && isMongoObjectId(pv);
              return {
                ...row,
                studio_design_id: ctx.design_id,
                studio_preview_id: ctx.preview_id,
                studio_design_name: ctx.design_name,
                studio_context_id: ctx.context_id,
                design_id: ctx.design_id,
                design_name: String(ctx.design_name ?? ""),
                preview_url: pv,
                ...(usePreviewForThumb ? { image_url: pv } : {}),
              };
            });
            return changed ? updated : prev;
          });
        };

        if (io && typeof io === "object") {
          const snapAny = io as Record<string, unknown>;
          const instDet = snapAny.installation_details as Record<string, unknown> | undefined;

          const { first, last } = splitFullName(String(snapAny.user_name ?? "").trim());
          setFieldValue("firstName", first, false);
          setFieldValue("lastName", last, false);
          const userEmailFromSnap = String(snapAny.user_email ?? "").trim();
          if (userEmailFromSnap) setFieldValue("userEmail", userEmailFromSnap, false);
          const userPhoneFromSnap = String(snapAny.user_phone ?? "").trim();
          if (userPhoneFromSnap) setFieldValue("userPhone", userPhoneFromSnap, false);

          if (instDet && typeof instDet === "object") {
            const { date } = parseIsoToDateAndTime(String(instDet.scheduled_date ?? ""));
            if (date) setFieldValue("scheduled_date", date, false);

            const instId = String(instDet.installer_id ?? "").trim();
            const instName = String(instDet.installer_name ?? "").trim();
            if (instId && instId !== "000000000000000000000000") {
              setSelectedInstaller({
                value: instId,
                label: instName || instId,
              });
              setFieldValue("installer", instId, false);
              setFieldValue("installeId", instId, false);
            }

            const instAddr = instDet.installation_address as Record<string, unknown> | undefined;
            if (instAddr && typeof instAddr === "object" && (instAddr.address_details || instAddr.label)) {
              const normalized = buildAddressesFromInstallationSnapshot(instAddr as Record<string, any>);
              setInstallationAddress((normalized ?? instAddr) as unknown as Addresses);
              setFieldValue(
                "installationAddressId",
                installationAddressDisplayFromSnapshot(instAddr as Record<string, any>),
                false
              );
            }
          }

          const billing = snapAny.billing_address as Record<string, unknown> | undefined;
          if (billing && typeof billing === "object") {
            setSelectedBillingAddress(billing as unknown as Addresses);
            const billingId = String(billing._id ?? "").trim();
            if (billingId) {
              // Ensure restored billing option is visible even if address list has not loaded yet.
              setAddressObjects((prev) => {
                const list = Array.isArray(prev) ? prev : [];
                const exists = list.some((a: any) => String(a?._id ?? "") === billingId);
                return exists ? list : ([billing as any, ...list]);
              });
            }
            const bid = String(billing._id ?? "").trim();
            if (bid && bid !== "000000000000000000000000") {
              setFieldValue("billingAddressId", bid, false);
            }
          }

          const bothSame = !!snapAny.both_address_same;
          setFieldValue("isSameAsBilling", bothSame, false);
          if (bothSame && billing && typeof billing === "object") {
            const bid = String(billing._id ?? "").trim();
            if (bid && bid !== "000000000000000000000000") {
              setFieldValue("installationAddressId", bid, false);
            }
          }

          const ai0 = (instDet?.additional_instructions as Array<{ value?: string }> | undefined)?.[0]?.value;
          const addl = snapAny.additional_instructions as Array<{ value?: string }> | undefined;
          const instr =
            typeof ai0 === "string" ? ai0 : typeof addl?.[0]?.value === "string" ? addl[0].value : "";
          setFieldValue("instructions", instr, false);

          setFieldValue("localLawsChecked", !!snapAny.local_laws_compliance, false);
          const prNorm = normalizeInstallationPriority(snapAny.priority);
          if (prNorm != null) setFieldValue("priority", prNorm, false);
          setFieldValue("gatecode", String(snapAny.gate_access_code ?? ""), false);

          const feeRows = Array.isArray(snapAny.specialfee) ? (snapAny.specialfee as Record<string, unknown>[]) : [];
          const feeUi = feeRows
            .map((f) => mapSavedInstallationSpecialFeeToUi(f))
            .filter((f) => f.id !== "");
          setSelectedSpecialFees(feeUi as any);
          setFieldValue("specialfee", feeUi, false);

          const sub = Number(snapAny.subtotal_amount ?? 0);
          const grand = Number(snapAny.total_amount ?? 0);
          const installCost = Number(snapAny.installation_cost ?? 0);
          const installTax = Number(snapAny.installation_tax_amount ?? 0);
          const overAllTax = Number(snapAny.over_all_tax_amount ?? 0);
          const discountAmount = Number(snapAny.discount_amount ?? 0);
          const feeSum = feeUi.reduce((s, f) => s + Number(f.amount ?? 0), 0);
          const feeTax = feeUi.reduce((s, f) => s + Number(f.tax_amount ?? 0), 0);
          const productTax = Math.max(0, overAllTax - installTax - feeTax);
          const taxAll = installTax + feeTax + productTax;
          setInstallationCost(Number.isFinite(installCost) ? installCost : null);
          setFieldValue("totals.installation_subtotal", sub, false);
          setFieldValue("totals.specialfee_subtotal", feeSum, false);
          setFieldValue("totals.installation_tax_amount", installTax, false);
          setFieldValue("totals.specialfee_tax_amount", feeTax, false);
          setFieldValue("totals.product_subtotal", Number((snapAny as any).product_subtotal ?? 0), false);
          setFieldValue("totals.product_tax_amount", Number((snapAny as any).product_tax_amount ?? productTax), false);
          setFieldValue("totals.grand_total", grand, false);
          setFieldValue("discount_amount", discountAmount, false);
          setFieldValue("product_discount_amount", Number((snapAny as any).product_discount_amount ?? 0), false);
          setFieldValue("discount_code", String(snapAny.discount_code ?? ""), false);
          setFieldValue("calc_install_rate_str", String(snapAny.installation_tax_percentage ?? ""), false);
          setFieldValue("calc_overall_rate_str", String(snapAny.over_all_tax_percentage ?? ""), false);
          setFieldValue("calc_fee_rate_str", feeSum > 0 ? String(feeTax / feeSum) : "", false);
          setFieldValue("subtotal", Math.max(0, sub + feeSum), false);
          setFieldValue("tax", taxAll, false);
          setFieldValue("total", Math.max(0, grand - discountAmount), false);
          const cid = String(snapAny.cart_id ?? "").trim();
          if (cid) setFieldValue("totals.cartId", cid, false);

          const ipRaw = snapAny.installation_post as Record<string, unknown> | undefined;
          if (ipRaw && typeof ipRaw === "object" && typeof ipRaw.post_type_id === "string") {
            const slots = Array.isArray(ipRaw.slots) ? ipRaw.slots : [];
            const ipPayload = {
              post_type_id: String(ipRaw.post_type_id),
              illustration: String(ipRaw.illustration ?? ""),
              slots: slots as import("pages/signage/PostTypeSignConfiguration").InstallationPostPayload["slots"],
            };
            setInstallationPost(ipPayload);
            if (slots.length > 0) {
              requestAnimationFrame(() => {
                postTypeConfigRef.current?.applyStudioSlotImagesFromPayload(ipPayload);
              });
            }
          }
        }

        mergeContextsIntoRows();

        if (!cancelled) restoredStudioReturnKeyRef.current = key;
      } catch (err) {
        safeConsole.warn("Failed to restore customization batch:", err);
        if (!cancelled) setStudioBatchLineItems([]);
      } finally {
        window.setTimeout(() => {
          isStudioRestoreApplyingRef.current = false;
        }, 350);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore when URL + auth are ready
  }, [studioReturnBatchId, studioReturnContextId, token, userInfo]);

  const installationStudioCustomization = useMemo(
    (): StudioCustomizationFlowProps => ({
      source: "signage_add_installation",
      existingBatchId: studioReturnBatchId.trim() || undefined,
      batchLineItems: studioBatchLineItems.length ? studioBatchLineItems : undefined,
      getInstallationOrderSnapshot: async () => {
        const built = await buildInstallationOrderPayloadRef.current?.(formikValuesForStudioSnapshotRef.current);
        return (built ?? {}) as Record<string, unknown>;
      },
    }),
    [studioReturnBatchId, studioBatchLineItems]
  );

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadDir", "installation_orders");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress((event.loaded / event.total) * 100);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 201) {
          const response = JSON.parse(xhr.responseText);
          resolve(response.path);
        } else {
          reject(new Error("Failed to upload file"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during file upload"));
      xhr.send(formData);
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (file) {
      formik.setFieldValue("image", file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleLocationChange = (
    selectedOption: SingleValue<{ value: string; label: string }>
  ) => {
    formik.setFieldValue("location", selectedOption?.value || "");
  };

  /** After the map mounts (or the layout width changes), tiles need a `resize` or the pane can stay gray. */
  const handleInstallationMapLoad = useCallback((map: google.maps.Map) => {
    installationMapRef.current = map;
    const syncView = () => {
      google.maps.event.trigger(map, "resize");
      const pin = selectedLocationRef.current;
      map.setCenter(pin ?? center);
    };
    syncView();
    requestAnimationFrame(syncView);
    window.setTimeout(syncView, 100);
    window.setTimeout(syncView, 400);
  }, []);

  // seting the total units based on the selected products
  useEffect(() => {
    const newTotal = selectedProducts.reduce((sum, product) => sum + (product.quantity || 0), 0);
    setTotalUnits(newTotal);
  }, [selectedProducts]);


  // seting the installation address based on the selected billing address
  useEffect(() => {
    if (formik.values.isSameAsBilling) {
      const billing = selectedBillingAddress;

      if (billing) {
        console.log("Billing Address:", billing);
        setInstallationAddress(billing);
        formik.setFieldValue("installationAddressId", `${billing.label}: ${billing?.address_details?.addressLine1}, ${billing?.address_details?.city}, ${billing?.address_details?.state}, ${billing?.address_details?.zip ? billing?.address_details?.zip : ''}`);
        triggerRecomputeIfReadyRef.current();
      }
    }
  }, [formik.values.isSameAsBilling, selectedBillingAddress]);

  // calculating the subtotal and total
  // useEffect(() => {
  //   const subtotal = Number(installationCost);
  //   const total = subtotal - Number(formik.values.discount_amount || 0);

  //   formik.setFieldValue("subtotal", subtotal);
  //   formik.setFieldValue("total", total);
  // }, [installationCost, formik.values.tax, formik.values.discount]);

  // remove the preview images
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  useEffect(() => {
    if (
      installationAddress?.address_details?.latitude &&
      installationAddress?.address_details?.longitude &&
      installationAddress?.address_details?.latitude !== 0 &&
      installationAddress?.address_details?.longitude !== 0
    ) {
      setSelectedLocation({
        lat: installationAddress?.address_details?.latitude,
        lng: installationAddress?.address_details?.longitude,
      });
    }
  }, [installationAddress]);

  useEffect(() => {
    const map = installationMapRef.current;
    if (!map || !selectedLocation) return;
    map.panTo(selectedLocation);
  }, [selectedLocation]);

  const handleMarkerDragEnd = async (event: google.maps.MapMouseEvent) => {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();

    if (!lat || !lng) return;

    setSelectedLocation({ lat, lng });

    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (response.results[0]) {
        const place = response.results[0];
        const addressComponents = place.address_components;

        const getComponent = (type: string) => {
          const component = addressComponents.find((c: any) =>
            c.types.includes(type)
          );
          return component
            ? { long_name: component.long_name, short_name: component.short_name }
            : { long_name: "", short_name: "" };
        };

        const streetNumber = getComponent("street_number").long_name;
        const route = getComponent("route").long_name;
        const cityObj =
          getComponent("locality") ||
          getComponent("administrative_area_level_2");
        const stateObj = getComponent("administrative_area_level_1");
        const postalCode = getComponent("postal_code").long_name;
        const countryObj = getComponent("country");

        const street = `${streetNumber} ${route}`.trim();

        const addressDetails: Address = {
          _id: "",
          label: place.formatted_address,
          addressLine1: street,
          addressLine2: "",
          country: countryObj.long_name,
          county: cityObj.long_name,
          city: cityObj.long_name,
          state: stateObj.long_name,
          zip: postalCode,
          cityCode: cityObj.short_name,
          stateCode: stateObj.short_name,
          countryCode: countryObj.short_name,
          latitude: lat,
          longitude: lng,
          phone: "",
        };

        // Build the full Addresses object (matches your imported type)
        const newAddresses: Addresses = {
          _id: "",
          user_id: "",
          user_type: "",
          label: place.formatted_address,
          first_name: "",
          last_name: "",
          company: "",
          default_address: false,
          address_details: addressDetails,
          created_by: {
            id: userId || "",
            name: userName || "",

          },
          updated_by: {
            id: userId || "",
            name: userName || "",
          },
        };

        setInstallationAddress(newAddresses);
        formik.setFieldValue("installationAddressId", place.formatted_address);
        // ✅ Automatically uncheck "Billing and Installation Address are Same" when user changes installation address via autocomplete
        formik.setFieldValue("isSameAsBilling", false);
        triggerRecomputeIfReadyRef.current();
      }
    } catch (error) {
      safeConsole.error("Geocoder failed", error);
      showError("Unable to update address from marker position.");
    }
  };

  const fetchLinkedProducts = async (
    signageCompanyId: string
  ) => {
    console.log("fetchLinkedProducts IN");
    if (!userId || !signageCompanyId) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/inventory/agent/${userId}/signage/${signageCompanyId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      console.log("Linked Products:", data);
      setProductObjs(data);

      if (!response.ok) {
        showError(data?.error || "Failed to fetch linked products");
      } else {
        // setLinkedProducts(data); if needed
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
      await handleApiError(err, {
        module: "installation",
        action: "Agent_Add_Installation_fetch_linked_products",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/inventory/agent/${userId}/signage/${signageCompanyId}`,
        method: "GET",
      }, token);
    }
  };

  // Fetch individual product by ID based on inventory_type
  const fetchProductById = async (
    inventoryType: "agent" | "office",
    userId: string,
    productId: string
  ): Promise<ProductObj | null> => {
    const url = `${process.env.REACT_APP_API_URL}/${inventoryType}/${userId}/inventory/${productId}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        // safeConsole.error(`Failed to fetch product ${productId}:`, errorData?.error || response.statusText);
        return null;
      }

      const data: ProductObj = await response.json();
      return data;
    } catch (err) {
      // safeConsole.error(`Error fetching product ${productId}:`, err);
      await handleApiError(err, {
        module: "installation",
        action: "Agent_Add_Installation_fetch_product_by_id",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: url,
        method: "GET",
        additionalInfo: { productId, inventoryType },
      }, token);
      return null;
    }
  };

  const handleSaveInstructions = (newInstructions: string) => {
    setInstructions(newInstructions);
    formik.setFieldValue("instructions", newInstructions);
  };



  // Tax related full code below i was write 

  // --- unique draft id for "add installation" ---
  const UNIQUE_ID_KEY = "re_installation_unique_id";

  async function ensureDraftUniqueId(token?: string): Promise<string | null> {
    try {
      // 1) if already cached, return it
      const existing = localStorage.getItem(UNIQUE_ID_KEY);
      if (existing) return existing;

      if (!token) return null;

      // 2) else fetch one from API and cache it
      const res = await fetch(`${process.env.REACT_APP_API_URL}/utils/new-unique-id`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) return null;

      const data = await res.json().catch(() => ({}));
      const id = data.id || data?.data?.id || data?.data?.unique_id || data?.unique_id || null;
      if (!id) return null;

      localStorage.setItem(UNIQUE_ID_KEY, id);
      return id;
    } catch (error) {
      await handleApiError(error, {
        module: "installation",
        action: "Agent_Add_Installation_ensure_draft_unique_id",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/utils/new-unique-id`,
        method: "GET",
      }, token);
      return null;
    }
  }

  // ✅ Call ensureDraftUniqueId when buyer type is set (from initialValues or programmatically)
  React.useEffect(() => {
    const currentType = formik.values.userType;
    if (!currentType || !token) return;

    const ensureId = async () => {
      try {
        const id = await ensureDraftUniqueId(token || "");
        if (id) {
          formik.setFieldValue("draft_unique_id", id, false);
        }
      } catch (err) {
        safeConsole.error("Error ensuring draft unique ID:", err);
      }
    };

    ensureId();
  }, [formik.values.userType, token]);


  // ✅ Simple: Just use the state directly (no Formik reads!)
  const getSelectedFees = () => {
    return selectedSpecialFees
      .map((f) => normalizeInstallationSelectedFee(f, allAvailableSpecialFees))
      .filter((f) => f.id !== "");
  };

  const keyForInstallationProduct = (p: { product_id: string; variant_id?: string }) => `${p.product_id}:${p.variant_id ?? ""}`;

  buildInstallationOrderPayloadRef.current = async (values: any) => {
        let lineItems: any[] = [];

        // 📦 1. Regular products (agent or office) — fetch only for linked; skip new/catalog
        const fetchedProductObjs: ProductObj[] = [];
        for (const product of selectedProducts) {
          if (product.is_new_catalog) continue;
          if (product.inventory_type && product.user_id && product.product_id) {
            const productData = await fetchProductById(
              product.inventory_type as "agent",
              product.user_id,
              product.product_id
            );
            if (productData) {
              fetchedProductObjs.push(productData);
            } else {
              throw new Error(`Failed to fetch details for product: ${product.name || product.product_id}`);
            }
          }
        }

        const round2 = (n: number) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
        const resolvedLineItemsSource =
          lastResolvedInstallationRef.current?.line_items?.length
            ? lastResolvedInstallationRef.current.line_items
            : ((values.resolved_line_items || []) as ResolvedInstallationLineItem[]);
        const resolvedLineMap = new Map(
          resolvedLineItemsSource.map((li) => [orderLineKey(li.product_id, li.variant_id), li])
        );
        const resolvedOrderForScope = lastResolvedInstallationRef.current;
        const submitDiscountScope = resolvedOrderForScope
          ? inferInstallationDiscountScopeFromResolved(resolvedOrderForScope)
          : inferInstallationDiscountScope({
              discountAppliesTo: values.discount_applies_to,
              discountCode: values.discount_code,
              discountAmount: values.discount_amount,
              productDiscountAmount: values.product_discount_amount,
              lineItems: resolvedLineItemsSource,
            });
        const submitDiscountCode = String(
          values.discount_code || resolvedOrderForScope?.discount_code || ""
        ).trim();
        const { rootDiscountCode, lineDiscountCode } = splitInstallationDiscountForPayload(
          submitDiscountCode,
          submitDiscountScope
        );

        lineItems = selectedProducts?.map((product) => {
          const quantity = Math.max(0, Number(product.quantity) || 0);
          const key = keyForInstallationProduct(product);

          if (product.is_new_catalog) {
            const resolved = resolvedLineMap.get(key);
            const prices = getPayloadUnitPrices(product);
            const payloadUnitRetail = prices.unit_retail_price;
            const changedUnitPrice = prices.changed_unit_price;
            const purchase_price = 0;
            const isManualLine = manualUnitPriceMap[key] !== undefined;
            const rowDiscTotal = isManualLine
              ? 0
              : round2(Number(resolved?.discount_on_total_quantity ?? 0));
            const perUnitDiscount = quantity > 0 ? round2(rowDiscTotal / quantity) : 0;
            const lineTax = round2(Number(resolved?.item_tax_info?.total_product_tax_amount ?? 0));
            const taxInfo = resolved?.item_tax_info ?? {
              tax_percentage: 0,
              unit_product_tax_amount: 0,
              total_product_tax_amount: 0,
              tax_details: null,
              taxable: (product as any).is_taxable !== false,
            };
            const newProductInventorySource = product.inventory_type || "agent";
            const newProductLocation = (product.location || []).map((loc: any) => ({
              quantity: Number(loc.quantity) || 0,
              warehouse_id: String(loc.warehouse_id ?? ""),
              warehouse_name: loc.warehouse_name,
              zone_id: loc.zone_id ?? "",
              zone_name: loc.zone_name,
              rack_id: loc.rack_id ?? "",
              rack_name: loc.rack_name,
              bin_id: loc.bin_id ?? "",
              bin_name: loc.bin_name,
            }));
            return {
              product_id: product.product_id,
              variant_id: product.variant_id ?? null,
              slot_id: product.is_accessory ? "" : (product.slot_id ?? ""),
              is_new_purchase: true,
              manual_unit_price_override: isManualLine,
              source_product_id: product.product_id,
              inventory_source: newProductInventorySource,
              warehouse_id: null,
              serial_ids: [],
              product_name: product.name || "",
              sku: product.sku || "",
              quantity,
              unit_price: round2(prices.unit_price),
              total_price: round2(Number(resolved?.total_price ?? changedUnitPrice * quantity + lineTax)),
              discount: round2(rowDiscTotal),
              tax_amount: round2(lineTax),
              production_status: product.productionStatus || "not started",
              is_production: false,
              is_rotational: false,
              current_status: "with_supplier",
              product_location_status: product.product_location_status || "",
              notes: (product as any).notes || "",
              rotational_allocated: 0,
              storage_locations: [{
                quantity,
                current_status: "with_supplier",
                location: newProductLocation,
              }],
              is_taxable: !!(product as any).is_taxable,
              is_accessory: !!product.is_accessory,
              images:
                String(product.design_id ?? "").trim() && String(product.preview_url ?? "").trim()
                  ? [{ url: String(product.preview_url).trim() }]
                  : (product as any).image_url
                    ? [{ url: (product as any).image_url }]
                    : [],
              unit_purchase_price: purchase_price,
              unit_retail_price: round2(payloadUnitRetail),
              changed_unit_price: round2(changedUnitPrice),
              discount_on_unit_quantity: perUnitDiscount,
              discount_on_total_quantity: round2(rowDiscTotal),
              discount_code:
                submitDiscountScope === "product"
                  ? String(resolved?.discount_code ?? lineDiscountCode ?? "").trim()
                  : "",
              item_sub_total: round2(changedUnitPrice * quantity),
              item_tax_info: taxInfo,
              ...(String(product.design_id ?? "").trim()
                ? {
                    design_id: String(product.design_id).trim(),
                    design_name: String(product.design_name ?? "").trim(),
                    preview_url: String(product.preview_url ?? "").trim(),
                  }
                : {}),
              ...(product.customizations ? { customizations: product.customizations } : {}),
            };
          }

          const matched = fetchedProductObjs.find((p) => p.product_id === product.product_id);
          const variant: any = matched?.product?.variants?.[0] || {};
          const unit_price = Number(matched?.product?.retail_price || 0);
          const qty = Number(product.quantity);
          const ActualbaseUnit = unit_price;
          const changedUnitPrice = ActualbaseUnit;
          const rowDiscTotal = 0;
          const perUnitDiscount = 0;
          const lineTax = 0;
          const taxInfo = {
            tax_percentage: 0,
            unit_product_tax_amount: 0,
            total_product_tax_amount: 0,
            tax_details: null,
            taxable: (product as any).is_taxable !== false,
          };

          const variantImages =
            String(product.design_id ?? "").trim() && String(product.preview_url ?? "").trim()
              ? [{ url: String(product.preview_url).trim(), default: true }]
              : (variant?.images && Array.isArray(variant.images))
                ? variant.images.map((img: any) => ({ url: img.url, alt_text: img.alt_text, default: img.default }))
                : ((product as any).image_url ? [{ url: (product as any).image_url, default: true }] : []);

          return {
            product_id: product.product_id,
            variant_id: variant?.variant_id,
            slot_id: product.is_accessory ? "" : (product.slot_id ?? ""),
            inventory_source: matched?.inventory_type || "",
            warehouse_id: null,
            serial_ids: [],
            product_name: variant?.variant_name || "",
            sku: variant?.sku || "",
            quantity: qty,
            unit_price: ActualbaseUnit,
            total_price: round2(changedUnitPrice * qty),
            discount: round2(rowDiscTotal),
            tax_amount: round2(lineTax),
            inventory_deducted: false,
            fulfillment_type: "stock",
            production_status: product.productionStatus || "not started",
            is_rotational: matched?.product?.is_rotational || false,
            is_production: matched?.product?.is_production || false,
            current_status: matched?.product?.current_status || "",
            rotational_allocated: 0,
            storage_locations: [
              {
                quantity: qty,
                product_location_status: matched?.linked_installer_details?.storage_locations?.[0]?.product_location_status || "",
                current_status: matched?.linked_installer_details?.storage_locations?.[0]?.product_location_status || "",
                assigned_at: new Date().toISOString(),
                location: [],
              }
            ],
            is_taxable: !!(product as any).is_taxable,
            is_accessory: !!product.is_accessory,
            images: variantImages,
            unit_retail_price: round2(ActualbaseUnit),
            changed_unit_price: round2(changedUnitPrice),
            discount_on_unit_quantity: perUnitDiscount,
            discount_on_total_quantity: round2(rowDiscTotal),
            item_sub_total: round2(changedUnitPrice * qty + lineTax),
            item_tax_info: taxInfo,
            ...(String(product.design_id ?? "").trim()
              ? {
                  design_id: String(product.design_id).trim(),
                  design_name: String(product.design_name ?? "").trim(),
                  preview_url: String(product.preview_url ?? "").trim(),
                }
              : {}),
          };
        });

        const lineItemsForSubmit =
          resolvedLineItemsSource.length > 0
            ? (mergeResolvedTaxIntoLineItems(lineItems, resolvedLineItemsSource) as typeof lineItems)
            : lineItems;
        const lineItemsMerged =
          submitDiscountScope === "installation"
            ? lineItemsForSubmit.map((li: any) =>
                li?.is_new_purchase === true ? { ...li, discount_code: "" } : li
              )
            : lineItemsForSubmit;

        const installTax = Number(values.totals.installation_tax_amount || 0);
        const feeTax = Number(values.totals.specialfee_tax_amount || 0);
        const baseInstallationCost = Math.max(0, parseFloat(String(installationCost)) || 0);
        const resolvedOrder = lastResolvedInstallationRef.current;
        const subtotalAmount = round2(
          Number(resolvedOrder?.subtotal_amount ?? baseInstallationCost + Number(values.totals.units_cost || 0))
        );
        const overAllTaxAmount = round2(
          Number(resolvedOrder?.over_all_tax_amount ?? Number(values.tax || installTax + feeTax))
        );
        const totalAmount = round2(
          Number(resolvedOrder?.total_amount ?? values.totals.grand_total ?? 0)
        );

        // ✅ Use state-based special fees (array format)
        const uiFees = (values?.specialfee || []) as Array<any>;
        console.log("uiFees", uiFees);
        const feeRows = uiFees.map((f) =>
          mapInstallationSpecialFeeForSubmit(f, { catalog: allAvailableSpecialFees })
        );

        console.log("feeRows:", selectedSpecialFees);

        const installationTaxPctStr = values.calc_install_rate_str || "";   // string
        const overallTaxPctStr = values.calc_overall_rate_str || "";       // string
        const specialFeeTaxPct = (() => {
          const s = values.calc_fee_rate_str;
          return s ? Number(s) : undefined;
        })();

        // console.log("installationTaxPctStr", {
        //   user_type: values.userType,
        //   user_name: userName,
        //   user_email: values.userEmail,
        //   user_phone: values.userPhone,
        //   order_number: null, // or generate dynamically
        //   status: 100,
        //   inventory_source: "agent",
        //   source_id: userId,
        //   brokerage_firm_id: null,
        //   brokerage_firm_name: null,
        //   brokerage_firm_admin: null,
        //   team_member_id: values.teamMemberId,
        //   team_member_name: values.teamMemberName,
        //   office: null,
        //   agent_id: userId,
        //   agent_name: userName,
        //   signage_id: selectedInstaller?.value || null,
        //   signage_company_name: selectedInstaller?.label || null,
        //   line_items: lineItems,
        //   customer_group_id: null,
        //   payment_method: values.payment_method,
        //   payment_status: "Pending",
        //   order_type: "installation",
        //   payment_info: [],
        //   installation_details: {
        //     scheduled_date: new Date(values.scheduled_date).toISOString(),
        //     installation_address: installationAddress,
        //     installer_id: selectedInstaller?.value || null,
        //     installer_name: null,
        //     contact_number: null,
        //     estimated_duration: null,
        //     actual_installation_date: null,
        //     additional_instructions: [
        //       {
        //         value: values.instructions,
        //         added_by: {
        //           id: userId,
        //           name: userName,
        //         },
        //         added_at: new Date().toISOString(),
        //       },
        //     ],
        //     assignee_id: null,
        //     tax_details: null,
        //   },
        //   billing_address: selectedBillingAddress,
        //   both_address_same: values.isSameAsBilling,
        //   additional_instructions: [
        //     {
        //       value: values.instructions,
        //       added_by: {
        //         id: userId,
        //         name: userName,
        //       },
        //       added_at: new Date().toISOString(),
        //     },
        //   ],
        //   local_laws_compliance: values.localLawsChecked,
        //   priority: values.priority,
        //   uploaded_files: uploadedFileMeta || [],
        //   additional_cost: 0,
        //   tax_amount: 0,
        //   overpaid_amount: 0,
        //   version: 1,
        //   tax_details: null,
        //   gate_access_code: values.gatecode || "",
        //   discount_amount: Number(values?.discount_amount || values?.discount || 0),
        //   discount_code: values?.discount_code || "",
        //   subtotal_amount: installSub,                   // SubtotalAmount
        //   installation_cost: parseFloat(String(installationCost)) || 0,  // InstallationCost
        //   specialfee: feeRows,                           // ✅ Array of special fees
        //   installation_tax_amount: installTax,           // InstallationTaxAmount
        //   installation_tax_percentage: installationTaxPctStr,     // InstallationTaxPercentage (string ptr)
        //   overall_tax_amount: installTax + feeTax,       // OverAllTaxAmount
        //   overall_tax_percentage: overallTaxPctStr,      // OverAllTaxPercentage (string ptr)
        //   tax_inclusive: false,                          // TaxInclusive
        //   total_amount: grand,
        //   tax_status: "draft",
        //   cart_id: UNIQUE_ID_KEY1,
        // });


        const installationOrderPayload = {
          user_type: values.userType,
          user_name: `${values.firstName || ""} ${values.lastName || ""}`.trim() || userName,
          user_email: values.userEmail,
          user_phone: values.userPhone,
          order_number: null, // or generate dynamically
          status: 100,
          inventory_source: "agent",
          source_id: userId,
          brokerage_firm_id: null,
          brokerage_firm_name: null,
          brokerage_firm_admin: null,
          team_member_id: values.teamMemberId,
          team_member_name: values.teamMemberName,
          office: null,
          agent_id: userId,
          agent_name: `${values.firstName || ""} ${values.lastName || ""}`.trim() || userName,
          signage_id: selectedInstaller?.value || null,
          signage_company_name: selectedInstaller?.label || null,
          line_items: lineItemsMerged,
          installation_post: installationPost ?? undefined,
          customer_group_id: null,
          payment_method: values.payment_method,
          payment_status: "Pending",
          order_type: "installation",
          po_number: String(values.po_number || "").trim(),
          payment_info: [],
          installation_details: {
            ...(values.scheduled_date
              ? { scheduled_date: new Date(values.scheduled_date).toISOString() }
              : {}),
            installation_address: installationAddress,
            installer_id: selectedInstaller?.value || null,
            installer_name: selectedInstaller?.label ?? null,
            contact_number: null,
            estimated_duration: null,
            actual_installation_date: null,
            additional_instructions: [
              {
                value: values.instructions,
                added_by: {
                  id: userId,
                  name: userName,
                },
                added_at: new Date().toISOString(),
              },
            ],
            assignee_id: selectedInstaller?.value ?? null,
            tax_details: null,
          },
          billing_address: selectedBillingAddress,
          both_address_same: values.isSameAsBilling,
          additional_instructions: [
            {
              value: values.instructions,
              added_by: {
                id: userId,
                name: userName,
              },
              added_at: new Date().toISOString(),
            },
          ],
          local_laws_compliance: values.localLawsChecked,
          priority: values.priority,
          uploaded_files: uploadedFileMeta || [],
          additional_cost: Number(resolvedOrder?.additional_cost ?? values.additional_cost ?? 0),
          tax_amount: totalAmount,
          overpaid_amount: 0,
          version: 1,
          tax_details: null,
          gate_access_code: values.gatecode || "",
          discount_amount:
            submitDiscountScope === "installation"
              ? Number(values?.discount_amount || values?.discount || resolvedOrderForScope?.discount_amount || 0)
              : 0,
          product_discount_amount:
            submitDiscountScope === "product"
              ? Number(values.product_discount_amount ?? values.discount_amount ?? 0)
              : 0,
          discount_code: submitDiscountScope === "installation" ? rootDiscountCode : "",
          discount_applies_to: submitDiscountScope,
          subtotal_amount: subtotalAmount,
          installation_cost: Number(resolvedOrder?.installation_cost ?? baseInstallationCost),
          specialfee: feeRows,
          installation_tax_amount: installTax,
          installation_tax_percentage: installationTaxPctStr || "",
          over_all_tax_amount: overAllTaxAmount,
          over_all_tax_percentage: overallTaxPctStr,
          tax_inclusive: dynamicTaxable,
          total_amount: totalAmount,
          tax_status: values.tax_status || resolvedOrder?.tax_status || "",
          cart_id: String(
            resolvedOrder?.cart_id ||
              values.draft_unique_id ||
              UNIQUE_ID_KEY1 ||
              ""
          ),
        };

    return overlayInstallationSubmitFromResolved(
      installationOrderPayload,
      lastResolvedInstallationRef.current
    );
  };


  const getResolvedLineItem = useCallback(
    (p: { product_id: string; variant_id?: string }) => {
      const key = keyForInstallationProduct(p);
      const rows = (formik.values.resolved_line_items || []) as ResolvedInstallationLineItem[];
      return rows.find((li) => orderLineKey(li.product_id, li.variant_id) === key);
    },
    [formik.values.resolved_line_items]
  );

  const getPayloadUnitPrices = useCallback(
    (p: { product_id: string; variant_id?: string; unit_price?: number }) => {
      const key = keyForInstallationProduct(p);
      const original = getOriginalCatalogUnitPrice(p);
      const resolved = getResolvedLineItem(p);
      const manual = manualUnitPriceMap[key];
      if (manual !== undefined) {
        const v = getResolveRequestUnitPrice(key, original, manualUnitPriceMap);
        return { unit_price: v, unit_retail_price: v, changed_unit_price: v };
      }
      const unitRetail = getResolveRequestUnitPrice(key, original, manualUnitPriceMap);
      const changed = Number(resolved?.changed_unit_price ?? unitRetail);
      return { unit_price: unitRetail, unit_retail_price: unitRetail, changed_unit_price: changed };
    },
    [manualUnitPriceMap, getResolvedLineItem]
  );

  const unitsCost = React.useMemo(() => {
    const purchasedLines = (selectedProducts || []).filter((p) => isPurchasedProductLine(p));
    return computeInstallationUnitsCostFromChangedUnitPrice({
      products: purchasedLines.map((p) => ({
        product_id: p.product_id,
        variant_id: p.variant_id,
        quantity: p.quantity,
        unit_price: p.unit_price,
        changed_unit_price: p.changed_unit_price,
        inventory_type: p.inventory_type,
        inventory_source: (p as { inventory_source?: string }).inventory_source,
        Price: Number(p.changed_unit_price ?? p.unit_price ?? 0),
      })),
      manualUnitPriceMap,
      getResolvedLineItem,
      isCustomProduct: (product) =>
        String(product?.inventory_type || product?.inventory_source || "").toLowerCase() === "custom",
    });
  }, [selectedProducts, manualUnitPriceMap, getResolvedLineItem]);
  const resolvedInstallCost = Number(
    formik.values.installation_cost ??
      formik.values.totals?.installation_subtotal ??
      installationCost ??
      0
  );
  const resolvedAdditionalCost = Number(formik.values.additional_cost ?? 0);
  const resolvedSubtotalAmount =
    resolvedInstallCost + resolvedAdditionalCost + unitsCost;

  const isRecomputingRef = React.useRef(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const recompute = React.useCallback(async (options?: { force?: boolean; discountCode?: string; fees?: any[] }): Promise<boolean> => {
    if (!dynamicCostId) return false;

    if (isRecomputingRef.current) {
      safeConsole.log("⏸️ Recompute already in progress, skipping...");
      return false;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const fees = options?.fees ?? getSelectedFees();
    const effectiveDiscountCode =
      options?.discountCode !== undefined
        ? String(options.discountCode || "").trim()
        : String(formik.values.discount_code || "").trim();

    const catalogLineSig = (selectedProducts || [])
      .filter((p) => p.is_new_catalog && p.product_id)
      .map((p) => {
        const key = keyForInstallationProduct(p);
        const original = getOriginalCatalogUnitPrice(p);
        const reqPrice = getResolveRequestUnitPrice(key, original, manualUnitPriceMap);
        return `${p.product_id}:${p.variant_id}:${p.quantity}:${reqPrice}`;
      })
      .join("|");

    const requestSignature = JSON.stringify({
      dynamicCostId,
      installationCost: Number(installationCost) || 0,
      discountCode: effectiveDiscountCode,
      feeIds: fees.map((f) => f.id).sort().join(","),
      feeAmounts: fees.map((f) => f.amount).join(","),
      dest: installationAddress?.address_details
        ? [
            installationAddress.address_details.addressLine1,
            installationAddress.address_details.addressLine2,
            installationAddress.address_details.city,
            installationAddress.address_details.state,
            installationAddress.address_details.stateCode,
            installationAddress.address_details.zip,
            installationAddress.address_details.latitude,
            installationAddress.address_details.longitude,
          ]
            .map((x) => String(x ?? "").trim())
            .join("-")
        : "",
      userId: userId || "",
      catalogLineSig,
    });

    if (!options?.force && lastRecomputeParamsRef.current === requestSignature) {
      safeConsole.log("⏸️ Same parameters as last recompute, skipping duplicate call");
      return true;
    }

    recomputeRequestIdRef.current += 1;
    const currentRequestId = recomputeRequestIdRef.current;
    lastRecomputeParamsRef.current = requestSignature;

    isRecomputingRef.current = true;
    setIsRecomputingTax(true);
    setCouponResolveError("");

    const allFeesSubtotal = fees.reduce((sum, f) => sum + Number(f.amount || 0), 0);

    try {
      if (currentRequestId !== recomputeRequestIdRef.current) return false;

      const signageId = String(selectedInstaller?.value || "").trim();
      const agentDisplayName = `${formik.values.firstName || ""} ${formik.values.lastName || ""}`.trim() || userName || "";

      const resolveDiscountCode = getInstallationResolveRootDiscountCode({
        discountCodeOverride: options?.discountCode,
        formikDiscountCode: formik.values.discount_code,
      });

      const resolveLineItems = buildInstallationAddResolveLineItems({
        catalogProducts: selectedProducts || [],
        manualUnitPriceMap,
        defaultInventorySource: "agent",
      });

      const resolvePayload = buildInstallationResolvePayload({
        cartId: String(formik.values.draft_unique_id || UNIQUE_ID_KEY1 || ""),
        dynamicCostId,
        signageId,
        sourceId: userId || "",
        customerId: userId || "",
        inventorySource: "agent",
        installationCost: Number(installationCost) || 0,
        installationTaxable: dynamicTaxable,
        discountCode: resolveDiscountCode,
        paymentMethod: formik.values.payment_method || "card",
        userType: formik.values.userType,
        userName: agentDisplayName,
        userEmail: formik.values.userEmail,
        userPhone: formik.values.userPhone,
        customerFirstName: formik.values.firstName,
        customerLastName: formik.values.lastName,
        customerEmail: formik.values.userEmail,
        customerPhone: formik.values.userPhone,
        agentId: userId || null,
        agentName: agentDisplayName,
        billingAddress: selectedBillingAddress as InstallationResolveAddress | null,
        installationAddress: installationAddress as InstallationResolveAddress | null,
        lineItems: resolveLineItems,
        specialFees: fees.map((f) => mapInstallationSpecialFeeForResolve(f, allAvailableSpecialFees)),
      });

      const resolved = await postInstallationOrderResolve(token || "", {
        order_type: "installation",
        action: "draft",
        payload: resolvePayload,
      });

      if (currentRequestId !== recomputeRequestIdRef.current) return false;

      if (!resolved || typeof resolved !== "object") {
        formik.setFieldValue("totals.installation_subtotal", Number(installationCost) || 0, false);
        formik.setFieldValue("totals.installation_tax_amount", 0, false);
        formik.setFieldValue("totals.specialfee_subtotal", allFeesSubtotal, false);
        formik.setFieldValue("totals.specialfee_tax_amount", 0, false);
        formik.setFieldValue("totals.grand_total", (Number(installationCost) || 0) + allFeesSubtotal, false);
        formik.setFieldValue("specialfee", fees, false);
        return false;
      }

      lastResolvedInstallationRef.current = resolved;
      isUpdatingFromRecomputeRef.current = true;
      const applied = applyInstallationResolveResponse(
        resolved,
        formik,
        Number(installationCost) || 0,
        {
          requestedDiscountCode: effectiveDiscountCode,
          existingFeeRows: fees,
          specialFeeCatalog: allAvailableSpecialFees,
        }
      );
      setSelectedSpecialFees(applied.mergedFees);
      const { code: resolvedCode, amount: resolvedDiscountAmount } = getResolvedDiscountDisplay(
        resolved,
        effectiveDiscountCode
      );
      if (resolvedCode) {
        setCouponInput(resolvedCode);
      } else if (!effectiveDiscountCode) {
        setCouponInput("");
      }
      if (!effectiveDiscountCode) {
        setAppliedVoucher(null);
        formik.setFieldValue("discount_code", "", false);
        formik.setFieldValue("discount_amount", 0, false);
        formik.setFieldValue("product_discount_amount", 0, false);
      } else if (resolvedCode && resolvedDiscountAmount > 0) {
        setAppliedVoucher({ code: resolvedCode, amount: resolvedDiscountAmount });
        formik.setFieldValue("discount_code", resolvedCode, false);
        formik.setFieldValue("discount_amount", resolvedDiscountAmount, false);
      }
      setResolveTick((t) => t + 1);
      return true;
    } catch (e) {
      safeConsole.error("[OrderResolve] failed:", e);
      setCouponResolveError(
        String((e as { message?: string })?.message ?? "Failed to calculate pricing")
      );
      formik.setFieldValue("totals.installation_subtotal", Number(installationCost) || 0, false);
      formik.setFieldValue("totals.installation_tax_amount", 0, false);
      formik.setFieldValue("totals.specialfee_subtotal", allFeesSubtotal, false);
      formik.setFieldValue("totals.specialfee_tax_amount", 0, false);
      formik.setFieldValue("totals.grand_total", (Number(installationCost) || 0) + allFeesSubtotal, false);
      formik.setFieldValue("specialfee", fees, false);
      return false;
    } finally {
      isRecomputingRef.current = false;
      setIsRecomputingTax(false);
    }
  }, [
    token,
    userId,
    userName,
    selectedInstaller?.value,
    installationCost,
    dynamicCostId,
    dynamicTaxable,
    getSelectedFees,
    installationAddress,
    selectedBillingAddress,
    formik,
    formik.values.draft_unique_id,
    formik.values.discount_code,
    formik.values.firstName,
    formik.values.lastName,
    formik.values.userEmail,
    formik.values.userPhone,
    formik.values.userType,
    formik.values.payment_method,
    selectedProducts,
    manualUnitPriceMap,
  ]);

  recomputeRef.current = recompute;

  const waitForRecomputeIdle = React.useCallback((maxMs = 10000) => {
    return new Promise<void>((resolve) => {
      const started = Date.now();
      const tick = () => {
        if (!isRecomputingRef.current || Date.now() - started > maxMs) {
          resolve();
          return;
        }
        window.setTimeout(tick, 50);
      };
      tick();
    });
  }, []);

  const applyDiscountCode = React.useCallback(
    async (rawCode: string) => {
      const code = String(rawCode || "").trim();
      if (!code) return;

      if (!selectedInstaller?.value || !installationAddress?.address_details) {
        showError("Complete installer and installation address before applying a discount.");
        return;
      }
      if (!dynamicCostId) {
        showError("Installation pricing is still loading. Please wait and try again.");
        return;
      }

      setCouponInput(code);
      formik.setFieldValue("discount_code", code, false);
      setCouponResolveError("");

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      await waitForRecomputeIdle();

      const ok = await recomputeRef.current?.({ force: true, discountCode: code });
      const display = getResolvedDiscountDisplay(lastResolvedInstallationRef.current, code);

      if (display.code && display.amount > 0) {
        setAppliedVoucher({ code: display.code, amount: display.amount });
        setCouponInput(display.code);
        formik.setFieldValue("discount_code", display.code, false);
        formik.setFieldValue("discount_amount", display.amount, false);
        setResolveTick((t) => t + 1);
        setCouponResolveError("");
        return;
      }

      setAppliedVoucher(null);
      if (!ok) {
        setCouponResolveError((prev) => prev || "Discount could not be applied. Please try again.");
      } else {
        setCouponResolveError("This discount code could not be applied.");
      }
    },
    [
      dynamicCostId,
      selectedInstaller?.value,
      installationAddress?.address_details,
      formik,
      waitForRecomputeIdle,
    ]
  );

  const clearDiscountCode = React.useCallback(async () => {
    setAppliedVoucher(null);
    setCouponResolveError("");
    setCouponInput("");
    formik.setFieldValue("discount_code", "", false);
    formik.setFieldValue("discount_amount", 0, false);
    formik.setFieldValue("product_discount_amount", 0, false);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    await waitForRecomputeIdle();
    await recomputeRef.current?.({ force: true, discountCode: "" });
  }, [formik, waitForRecomputeIdle]);

  const discountDisplay = React.useMemo(
    () =>
      getOrderDiscountFieldDisplay({
        formikCode: formik.values.discount_code,
        formikAmount: formik.values.discount_amount,
        couponInput,
        appliedVoucher,
        resolved: lastResolvedInstallationRef.current,
      }),
    [
      appliedVoucher,
      formik.values.discount_code,
      formik.values.discount_amount,
      couponInput,
      resolveTick,
    ]
  );

  // keep these fields in sync with engine totals


  const ready = React.useMemo(
    () =>
      Boolean(
        selectedInstaller?.value &&
        installationAddress?.address_details &&
        !Number.isNaN(installationCost)
      ),
    [selectedInstaller?.value, installationAddress?.address_details, installationCost]
  );

  // ---- Debounced trigger (runs only when ready) ----------------------

  const triggerRecomputeIfReady = React.useCallback(() => {
    if (!ready) return; // ✅ guard
    
    // ✅ Don't schedule if recompute is already running
    if (isRecomputingRef.current) {
      safeConsole.log("⏸️ Recompute in progress, skipping debounced call");
      return;
    }
    
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // ✅ Double-check before calling (in case state changed during debounce)
      if (!isRecomputingRef.current) {
        recompute();
      }
    }, 300);
  }, [ready, recompute]);

  triggerRecomputeIfReadyRef.current = triggerRecomputeIfReady;

  // optional: clear any pending timer on unmount
  React.useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  // ---- Watch address fields (but only run when ready) ----------------
  React.useEffect(() => {
    if (isRecomputingRef.current) {
      return;
    }
    triggerRecomputeIfReady();
  }, [
    selectedInstaller?.value,
    installationAddress?.address_details,
    formik.values.destination?.addressLine1,
    formik.values.destination?.city,
    formik.values.destination?.state,
    formik.values.destination?.stateCode,
    formik.values.destination?.zip,
    installationCost,
    (selectedProducts || [])
      .filter((p) => p.is_new_catalog && p.product_id)
      .map((p) => {
        const key = keyForInstallationProduct(p);
        const original = getOriginalCatalogUnitPrice(p);
        const reqPrice = getResolveRequestUnitPrice(key, original, manualUnitPriceMap);
        return `${p.product_id}:${p.variant_id}:${p.quantity}:${reqPrice}`;
      })
      .join("|"),
    triggerRecomputeIfReady,
  ]);

  const prevFeesRef = React.useRef<string>("");

  React.useEffect(() => {
    if (isRecomputingRef.current) {
      return;
    }
    if (isUpdatingFromRecomputeRef.current) {
      isUpdatingFromRecomputeRef.current = false;
      return;
    }

    const feeSig = selectedSpecialFees
      .map((f) => `${f.id}:${Number(f.amount || 0)}`)
      .sort()
      .join(",");
    if (prevFeesRef.current === feeSig) return;
    prevFeesRef.current = feeSig;

    if (!ready) return;
    triggerRecomputeIfReady();
  }, [selectedSpecialFees, ready, triggerRecomputeIfReady]);

  React.useEffect(() => {
    const t = formik.values.totals || {};
    const subAll = Number(t.installation_subtotal || 0) + Number(t.specialfee_subtotal || 0);
    const taxAll = Number(t.installation_tax_amount || 0) + Number(t.specialfee_tax_amount || 0);
    const grand = Number(t.grand_total || 0);
    const productTax = Number(formik.values.totals?.product_tax_amount || 0);

    formik.setFieldValue("subtotal", subAll, false);
    formik.setFieldValue("tax", taxAll + productTax, false);
    formik.setFieldValue("total", Math.max(0, grand), false);
  }, [
    formik.values.totals.installation_subtotal,
    formik.values.totals.specialfee_subtotal,
    formik.values.totals.installation_tax_amount,
    formik.values.totals.specialfee_tax_amount,
    formik.values.totals.grand_total,
    formik.values.totals.product_tax_amount,
  ]);

  if (loadError) {
    return (
      <div className="content container">
        <div className="alert alert-danger m-4" role="alert">
          Google Maps could not be loaded. Verify Google error in cons.
        </div>
      </div>
    );
  }

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="content container">
      <div className="">
        <h3 className="m-0 fw-bold addheaders">Installation Job Request</h3>
      </div>
      <div className="my-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap col-lg-12 os-card" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)', borderRadius: '12px', padding: '20px', backgroundColor: 'white' }}>
          {/* Left: Installation Status Tracker */}
          <div className="flex-grow-1">
            <InstallationStatusTracker type="installation" status={0} className="w-100" />
          </div>
        </div>
      </div>
      <form
        onSubmit={formik.handleSubmit}
        onKeyDown={(e) => {
          const t = e.target as HTMLElement;
          const tag = t.tagName;
          if (e.key === "Enter" && tag !== "BUTTON" && tag !== "TEXTAREA") {
            e.preventDefault();
          }
        }}
        className="me-2 me-lg-0"
      >
        <div className="row p-3 col-lg-12 card-body shadow-lg pb-4 mb-4 ms-0">
          <div className="mb-3">
            <SectionHeader title="Customer Information" icon="ri-user-3-line" color="#155dfc" size="md" divider />
          </div>
          <div className="col-lg-3 mb-3">
            <label htmlFor="firstName" className="form-label table-headers">
              First Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control table-cells ${(formik.submitCount > 0 || formik.touched.firstName) && formik.errors.firstName ? "is-invalid" : ""}`}
              id="firstName"
              name="firstName"
              placeholder="Enter first name"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              readOnly
            />
            {(formik.submitCount > 0 || formik.touched.firstName) && formik.errors.firstName && (
              <div className="invalid-feedback d-block">{formik.errors.firstName}</div>
            )}
          </div>

          <div className="col-lg-3 mb-3">
            <label htmlFor="lastName" className="form-label table-headers">
              Last Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control table-cells ${(formik.submitCount > 0 || formik.touched.lastName) && formik.errors.lastName ? "is-invalid" : ""}`}
              id="lastName"
              name="lastName"
              placeholder="Enter last name"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              readOnly
            />
            {(formik.submitCount > 0 || formik.touched.lastName) && formik.errors.lastName && (
              <div className="invalid-feedback d-block">{formik.errors.lastName}</div>
            )}
          </div>

          <div className="col-lg-3 mb-3">
            <label htmlFor="userEmail" className="form-label table-headers">Email <span className="text-danger">*</span></label>
            <input
              type="email"
              className={`form-control table-cells ${(formik.submitCount > 0 || formik.touched.userEmail) && formik.errors.userEmail ? "is-invalid" : ""}`}
              id="userEmail"
              name="userEmail"
              placeholder="Enter email"
              value={formik.values.userEmail}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {(formik.submitCount > 0 || formik.touched.userEmail) && formik.errors.userEmail && (
              <div className="invalid-feedback d-block">{formik.errors.userEmail}</div>
            )}
          </div>

          <div className="col-lg-3 mb-3">
            <label htmlFor="userPhone" className="form-label  table-headers">
              Phone <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control table-cells ${(formik.submitCount > 0 || formik.touched.userPhone) && formik.errors.userPhone ? "is-invalid" : ""}`}
              id="userPhone"
              name="userPhone"
              placeholder="Enter phone"
              value={formik.values.userPhone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {(formik.submitCount > 0 || formik.touched.userPhone) && formik.errors.userPhone && (
              <div className="invalid-feedback d-block">{formik.errors.userPhone}</div>
            )}
          </div>

        </div>
        <div className="d-block d-lg-flex gap-3">
          <div className="col-lg-8">
            <div className="row col-lg-12 card-body shadow-lg mb-4 ms-0 p-3">
              <div className="mb-3">
                <SectionHeader title="Schedule & Assignment" icon="ri-calendar-line" color="#00a63e" size="md" divider />
              </div>
              <div className="col-lg-4 mb-3">
                <label className="form-label table-headers">
                  Select Date
                </label>
                <input
                  type="date"
                  className={`form-control ${formik.touched.scheduled_date &&
                    formik.errors.scheduled_date
                    ? "is-invalid"
                    : ""
                    }`}
                  id="scheduled_date"
                  name="scheduled_date"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.scheduled_date}
                />
                {formik.touched.scheduled_date && formik.errors.scheduled_date && (
                  <div className="invalid-feedback">{formik.errors.scheduled_date}</div>
                )}
              </div>
              <div className="col-lg-4 mb-3">
                <label className="form-label table-headers">
                  Select Vendor <span className="text-danger">*</span>
                </label>
                <AsyncPaginate
                  id="installer"
                  classNamePrefix="rs"
                  placeholder="Search vendors…"
                  value={selectedInstaller}
                  loadOptions={loadVendorOptions}
                  onChange={(selected) => {
                    const nextInstallerId = selected?.value || "";
                    const currentInstallerId = selectedInstaller?.value || "";
                    formik.setFieldValue("installer", selected?.value || "");
                    if (nextInstallerId !== currentInstallerId) {
                      setSelectedProducts([]);
                      setProductObjs([]);
                      setSelectedProductOption(null);
                      setInstallationPost(null);
                      setManualUnitPriceMap({});
                      postTypeConfigRef.current = null;
                    }
                    setSelectedInstaller(selected);
                  }}
                  onBlur={() => formik.setFieldTouched("installer", true)}
                  additional={{ page: 1 }}
                  defaultOptions
                  debounceTimeout={300}
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
                {(formik.submitCount > 0 || formik.touched.installer) && formik.errors.installer && (
                  <div className="text-danger">{formik.errors.installer}</div>
                )}
              </div>
              {/* Priority */}
              <div className="col-lg-4 mb-3">
                <label className="form-label table-headers">Priority <span className="text-danger">*</span></label>
                <Select
                  id="priority"
                  className=""
                  name="priority"
                  options={[
                    { value: 1, label: "High" },
                    { value: 2, label: "Medium" },
                    { value: 3, label: "Low" },
                  ]}
                  value={
                    [
                      { value: 1, label: "High" },
                      { value: 2, label: "Medium" },
                      { value: 3, label: "Low" },
                    ].find((o) => Number(o.value) === Number(formik.values.priority)) || null
                  }
                  onChange={(option) => formik.setFieldValue("priority", option?.value || "")}
                  onBlur={() => formik.setFieldTouched("priority", true)}
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
                {(formik.submitCount > 0 || formik.touched.priority) && formik.errors.priority && (
                  <div className="text-danger">{formik.errors.priority}</div>
                )}
              </div>
            </div>
            <div className="row col-lg-12 mb-4 card-body shadow-lg mb-4 ms-0 pb-3 p-3">
              <div className="col-lg-12 d-flex mb-3">
                {/* Address: teal */}
                <SectionHeader title="Address Information" icon="ri-map-pin-line" color="#e7000b" size="md" divider className="col-lg-4" />
                <div className="align-items-baseline col-lg-8 d-flex mb-3 justify-content-end">
                  <div className="align-items-baseline col-lg-6 d-flex justify-content-end">
                    <label className="me-2">Gate Code</label>
                    <input type="text" id="gatecode" name="gatecode" className="form-control w-50" value={formik.values.gatecode} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="Enter Gate Code" />
                  </div>
                </div>
              </div>

              <div className="col-lg-12">
                <div className="align-items-baseline col-lg-12 d-flex justify-content-between">
                  <div className="d-flex fw-bold gap-2 my-2">
                    <input
                      type="checkbox"
                      id="isSameAsBilling"
                      name="isSameAsBilling"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      checked={formik.values.isSameAsBilling}
                    />{" "}
                    Billing and Installation Address are Same

                  </div>
                  <button
                    type="button"
                    className="btn btn-primary table-addbutton"
                    onClick={() => setShowAddressForm(true)}
                    // disabled={Boolean(formik.values.billingAddressId)}
                    title={formik.values.billingAddressId ? "Billing address selected" : "Add new address"}
                  >
                    + Address
                  </button>
                </div>
                <div className="row ms-2">
                  {/* ✅ Billing Address */}
                  <div className="col-lg-6" id="row-overlap">
                    <div className="mb-3">
                      <label htmlFor="billingAddressId" className="form-label table-headers">
                        Billing Address <span className="text-danger">*</span>
                      </label>

                      <Select
                        id="billingAddressId"
                        name="billingAddressId"   // ✅ Matches Yup schema
                        options={addressOptions}
                        value={
                          addressOptions?.find(
                            (option) => option.value === formik.values.billingAddressId
                          ) || null
                        }
                        onChange={(selected) => {
                          formik.setFieldValue("billingAddressId", selected?.value);
                          const selectedAddress = addressObjects?.find((addr) => addr._id === selected.value);
                          setSelectedBillingAddress(selectedAddress);
                        }}
                        onBlur={() => formik.setFieldTouched("billingAddressId", true)}
                        components={{ DropdownIndicator }}
                        styles={customSelectStyles}
                      />

                      {/* ✅ Show Billing Address error */}
                      {formik.touched.billingAddressId && formik.errors.billingAddressId && (
                        <div className="text-danger mt-1">
                          {formik.errors.billingAddressId}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ✅ Installation Address - only if checkbox is NOT checked */}
                  {formik.values.isSameAsBilling === false && (
                    <div className="col-lg-6 mb-3" id="row-overlap">
                      <div className="">
                        <label htmlFor="installationAddressId" className="form-label">
                          Installation Address <span className="text-danger">*</span>
                        </label>

                        <AddressAutocomplete
                          id="installationAddressId"
                          name="installationAddressId"
                          value={formik.values.installationAddressId}
                          onChange={(v) => formik.setFieldValue("installationAddressId", v)}
                          onSelect={(picked) => {
                            const installation_address = `${picked.addressLine1}, ${picked.city}, ${picked.state}, ${picked.zipcode}`;

                            const addressDetails: Address = {
                              _id: "",
                              label: installation_address,
                              addressLine1: picked.addressLine1,
                              addressLine2: "",
                              country: picked.country,
                              county: picked.city,
                              city: picked.city,
                              state: picked.state,
                              zip: picked.zipcode,
                              cityCode: picked.cityCode,
                              stateCode: picked.stateCode,
                              countryCode: picked.countryCode,
                              latitude: picked.latitude,
                              longitude: picked.longitude,
                              phone: "",
                            };

                            const newAddresses: Addresses = {
                              _id: "",
                              user_id: "",
                              user_type: "",
                              label: installation_address,
                              first_name: "",
                              last_name: "",
                              company: "",
                              default_address: false,
                              address_details: addressDetails,
                              created_by: {
                                id: userId || "",
                                name: userName || "",
                              },
                              updated_by: {
                                id: userId || "",
                                name: userName || "",
                              },
                            };

                            setInstallationAddress(newAddresses);
                            formik.setFieldValue('installationAddressId', installation_address);
                            formik.setFieldValue("isSameAsBilling", false);
                            triggerRecomputeIfReady();
                          }}
                          className={`form-control table-cells ${((formik.submitCount > 0 || formik.touched.installationAddressId) && formik.errors.installationAddressId) ? "is-invalid" : ""}`}
                          placeholder="Enter installation address"
                          error={(formik.submitCount > 0 || formik.touched.installationAddressId) && formik.errors.installationAddressId ? (formik.errors.installationAddressId as string) : undefined}
                        />
                      </div>
                    </div>
                  )}
                  <div className="col-lg-12 d-flex justify-content-end pe-4 mb-3" style={{ marginTop: "0px !important" }}>
                    <button
                      type="button"
                      className={`btn btn-sm rounded-pill d-inline-flex align-items-center gap-2 shadow-sm ${isMarkerDraggable ? "btn-success" : "btn-outline-primary"}`}
                      title={`${isMarkerDraggable ? "Disable map edit" : "Enable map edit"}`}
                      onClick={() => {
                        const newValue = !isMarkerDraggable;
                        setIsMarkerDraggable(newValue);
                        showSuccess(
                          newValue
                            ? "You can now move the marker on the map."
                            : "Marker dragging disabled."
                        );
                      }}
                    >
                      <i className="ri-map-2-line fs-17"></i>
                      <i className="ri-edit-2-line fs-17"></i>
                      <span className="small fw-semibold">{isMarkerDraggable ? "Editing" : "Edit Map"}</span>
                    </button>
                  </div>
                  {/* ✅ Map Marker Toggle */}
                  {/* <div className="col-lg-2" id="row-overlap">
                    <p>&nbsp;</p>
                    <i
                      className="ri-search-line fs-3 text-bg-info"
                      style={{ cursor: "pointer" }}
                      title={`${isMarkerDraggable ? "Disable Marker Drag" : "Enable Marker Drag"
                        }`}
                      onClick={() => {
                        const newValue = !isMarkerDraggable;
                        setIsMarkerDraggable(newValue);
                        showError(
                          newValue
                            ? "You can now move the marker on the map."
                            : "Marker dragging disabled."
                        );
                      }}
                    ></i>
                  </div> */}
                </div>
              </div>
              <div className="col-lg-12" style={{ overflow: "hidden" }}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  zoom={10}
                  center={selectedLocation || center}
                  onLoad={handleInstallationMapLoad}
                >
                  {selectedLocation ? (
                    <Marker
                      position={selectedLocation}
                      draggable={isMarkerDraggable}
                      onDragEnd={handleMarkerDragEnd}
                    />
                  ) : null}
                </GoogleMap>
                {formik.touched.location && formik.errors.location && (
                  <div className="text-danger">{formik.errors.location}</div>
                )}
              </div>
            </div>
            {/* <div className="col-lg-12">
          <h4 className="ms-3">Product Selection</h4>
          <div className="row mb-3">
            <div className="col-lg-4 ms-4" id="row-overlap">
              <Select
                id="products"
                name="products"
                options={productOptions}
                value={selectedProductOption}
                onChange={(selected) => {
                  setSelectedProductOption(selected)
                }}
                onBlur={formik.handleBlur}
              />
            </div>
            <div className="col-lg-3" id="row-overlap">
              <button
                className="btn btn-info"
                type="button"
                onClick={() => {
                  if (!selectedProductOption) return;

                  const alreadyExists = selectedProducts.some(
                    (prod) => prod.product_id === selectedProductOption.value
                  );
                  if (alreadyExists) {
                    showError("Product already added");
                    return;
                  }

                  const fullProduct = productObjs.find(
                    (p) => p.product_id === selectedProductOption.value
                  );

                  if (fullProduct) {
                    const flattenedProduct = {
                      product_id: fullProduct.product_id,
                      name: fullProduct.product.name,
                      sku: fullProduct.product.sku || "",
                      inventory_type: fullProduct.inventory_type,
                      product_location_status: fullProduct.product.product_location_status,
                      user_id: fullProduct.agent_id || fullProduct.office_id,
                      quantity: 1,
                      updateStatus: "",
                      productionStatus: "",
                    };

                    setSelectedProducts((prev) => [...prev, flattenedProduct]);
                    setSelectedProductOption(null);
                  }
                  console.log("selectedProducts", selectedProducts);
                }}

              >
                Add Product
              </button>
            </div>
          </div>
        </div> */}

                {/* Post Type & Sign Configuration — same as Realtor/Signage */}
                <PostTypeSignConfiguration
                  key={pickerSignageCompanyId || "no-vendor"}
                  type="add"
                  vendorId={pickerSignageCompanyId}
                  token={token || ""}
                  postTypeOrderContext={postTypeOrderContext}
                  pickerUserType={pickerUserType}
                  pickerUserId={pickerUserId}
                  pickerEndUserId={userId || undefined}
                  pickerSignageCompanyId={pickerSignageCompanyId}
                  studioCustomization={installationStudioCustomization}
                  selectedStockLines={selectedProducts.map((p) => ({
                    product_id: String(p.product_id ?? ""),
                    variant_id: p.variant_id,
                    quantity: Number(p.quantity) || 1,
                  }))}
                  accessoryCount={selectedProducts.filter((p) => p.is_accessory).reduce((sum, p) => sum + (Number(p.quantity) || 0), 0)}
                  onPostTypeChange={() => {
                    setSelectedProducts([]);
                    setProductObjs([]);
                    setInstallationPost(null);
                  }}
                  onClearSlotReady={(api) => { postTypeConfigRef.current = api; }}
                  onInstallationPostChange={setInstallationPost}
                  onSlotDuplicateProduct={() =>
                    showError("This product is already selected. You cannot assign the same product to multiple slots.")
                  }
                  onSlotRemoved={(slotKey) => {
                    setSelectedProducts((prev) => prev.filter((p) => p.slotKey !== slotKey));
                  }}
                  onSlotProductSelect={(product: LinkedProduct, slotKey: string, slotId?: string) => {
                    const isLinked = product.linked_mode !== "catalog" && (product.inventory_source === "agent" || product.inventory_source === "office");
                    const isNewCatalog = product.linked_mode === "catalog";
                    const inventoryTypeForNew =
                      isNewCatalog && pickerUserType
                        ? pickerUserType === "agent"
                          ? "agent"
                          : "office"
                        : product.inventory_source || "";
                    const hasDesign = !!String(product.design_id ?? "").trim();
                    const previewId = String(product.preview_url ?? "").trim();
                    const imageForRow =
                      hasDesign && previewId ? previewId : product.images?.[0]?.url || "";
                    const flattened: FlattenedProduct = {
                      product_id: product.product_id,
                      name: product.name,
                      sku: product.sku,
                      inventory_type: inventoryTypeForNew,
                      current_status: isNewCatalog ? "with_supplier" : product.current_status,
                      availableqty: product.available_quantity ?? 0,
                      quantity: 1,
                      image_url: imageForRow,
                      location: product.location,
                      is_accessory: slotKey === "accessory",
                      ...(slotKey !== "accessory" ? { slotKey, slot_id: slotId ?? "" } : { slot_id: "" }),
                      ...(isLinked && pickerUserId ? { user_id: pickerUserId } : {}),
                      ...(product.variant_id ? { variant_id: String(product.variant_id).trim() } : {}),
                      ...(isNewCatalog
                        ? {
                            is_new_catalog: true,
                            variant_id: product.variant_id,
                            unit_price: product.retail_price != null ? Number(product.retail_price) : undefined,
                            is_taxable: (product as any).is_taxable !== false,
                            ...(product.customizations ? { customizations: product.customizations } : {}),
                          }
                        : {}),
                      ...(hasDesign
                        ? {
                            design_id: String(product.design_id ?? "").trim(),
                            design_name: String(product.design_name ?? "").trim(),
                            preview_url: previewId,
                          }
                        : {}),
                    };
                    handleAddFromPicker(flattened);
                  }}
                />

            <div className="row col-lg-12 mb-lg-5 card-body shadow-lg mb-3 ms-0 p-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex">
                  <span
                    aria-hidden="true"
                    className="image-icons"
                    style={{
                      backgroundColor: "#9810fa",
                      WebkitMaskImage: `url(${ProductSelection})`,
                      maskImage: `url(${ProductSelection})`,
                    }}
                  />
                  <SectionHeader title="Product Selection" icon="" variant="success" size="md" divider />
                </div>
                <div className="d-flex align-items-center gap-2">
                  {/* <button
                    type="button"
                    className="btn btn-primary table-addbutton br-8"
                    onClick={() => setPickerOpen(true)}
                    disabled={!canShowAdd}
                    title={canShowAdd ? "Add Product" : "Select vendor first"}
                  >
                    <i className="ri-add-line me-1" /> Add Product
                  </button> */}
                </div>
              </div>
              <div className="col-lg-12">
                {filtered.length === 0 ? (
                  <div className="text-center text-muted py-4 border rounded-3">
                    No products added yet.
                  </div>
                ) : (
                  (() => {
                    const inventoryProducts = filtered.filter((p) => !isPurchasedProductLine(p));
                    const purchasedProducts = filtered.filter((p) => isPurchasedProductLine(p));
                    const renderCard = (item: typeof filtered[0], displayIndex: number, isPurchasedLine: boolean) => {
                      const rowKey = item.rowKey || item.product_id;
                      const qtyNum = Math.max(1, Number(item.quantity) || 0);
                      const priceKey = keyForInstallationProduct(item);
                      const resolvedLine = isPurchasedLine ? getResolvedLineItem(item) : undefined;
                      const rowDiscount = isPurchasedLine
                        ? getInstallationLineDiscountDisplay(
                            priceKey,
                            item,
                            manualUnitPriceMap,
                            resolvedLine
                          )
                        : 0;
                      const lineTaxAmount = Number(
                        resolvedLine?.item_tax_info?.total_product_tax_amount ?? 0
                      );
                      const lineTaxable =
                        resolvedLine?.item_tax_info?.taxable ?? item.is_taxable ?? false;
                      const displayUnitCost = isPurchasedLine
                        ? getInstallationLineChangedUnitCostTotal(
                            priceKey,
                            item,
                            manualUnitPriceMap,
                            resolvedLine
                          )
                        : 0;
                      return (
                      <div key={rowKey} className="mb-3 p-3 rounded-3 border shadow-sm bg-gray-50">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <h6 className="m-0">Product #{displayIndex}</h6>
                          </div>
                          <button
                            type="button"
                            className="btn btn-link text-danger p-0"
                            title="Remove"
                            onClick={() => removeRow(rowKey)}
                          >
                            <i className="ri-delete-bin-line fs-5"></i>
                          </button>
                        </div>
                        <div className="row g-3">
                        <div className="col-lg-4">
                          <label className="table-cells">Product Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={item.name || item.product_name || ""}
                            disabled
                            readOnly
                          />
                        </div>
                        <div className="col-lg-4">
                          <label className="table-cells">SKU/Product Code</label>
                          <input
                            type="text"
                            className="form-control"
                            value={item.sku || ""}
                            disabled
                            readOnly
                          />
                        </div>
                        <div className="col-lg-4">
                          <label className="table-cells">Production Status</label>
                          <input
                            type="text"
                            className="form-control"
                            value={item.productionStatus || "--"}
                            disabled
                            readOnly
                          />
                        </div>
                        <div className="col-lg-4">
                          <label className="table-cells">Avl Qty</label>
                          <input
                            type="number"
                            className="form-control"
                            value={Number(item.availableqty ?? 0)}
                            disabled
                            readOnly
                          />
                        </div>
                        <div className="col-lg-4">
                          <label className="table-cells">Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            min={0}
                            placeholder="0"
                            value={item.quantity || ''}
                            disabled={!item.is_accessory}
                            readOnly={!item.is_accessory}
                            onChange={(e) => {
                              if (!item.is_accessory) return;
                              const rawValue = e.target.value;
                              const qty = rawValue === '' ? '' : Number(rawValue);
                              setSelectedProducts((prev) =>
                                prev.map((p) =>
                                  (p.rowKey || p.product_id) === rowKey ? { ...p, quantity: qty } : p
                                )
                              );
                              if (item.is_new_catalog && item.product_id && item.variant_id) {
                                const priceKey = keyForInstallationProduct(item);
                                setManualUnitPriceMap((prev) => {
                                  const next = { ...prev };
                                  delete next[priceKey];
                                  return next;
                                });
                                triggerRecomputeIfReady();
                              }
                            }}
                            onBlur={(e) => {
                              if (!item.is_accessory) return;
                              const currentValue = e.target.value;
                              if (currentValue === '' || Number(currentValue) <= 0) {
                                setSelectedProducts((prev) =>
                                  prev.map((p) =>
                                    (p.rowKey || p.product_id) === rowKey ? { ...p, quantity: 1 } : p
                                  )
                                );
                              }
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                        </div>
                        {isPurchasedLine ? (
                          <div className="col-lg-4">
                            <label className="table-cells">Unit Cost</label>
                            <input
                              type="text"
                              className="form-control text-center"
                              value={displayUnitCost > 0 ? displayUnitCost.toFixed(2) : ""}
                              readOnly
                              disabled
                            />
                          </div>
                        ) : (
                          <div className="col-lg-4">
                            <label className="table-cells">Unit Cost</label>
                            <input
                              type="text"
                              className="form-control text-center"
                              value="-"
                              disabled
                              readOnly
                            />
                          </div>
                        )}
                        {isPurchasedLine && (
                          <div className="col-lg-4">
                            <label className="table-cells">Discount</label>
                            <div className="d-flex align-items-center flex-wrap gap-2 py-1">
                              <DiscountAmountDisplay amount={rowDiscount} />
                            </div>
                          </div>
                        )}
                        {isPurchasedLine && (
                          <div className="col-lg-4">
                            <label className="table-cells">Tax</label>
                            <input
                              type="text"
                              className="form-control text-center"
                              value={
                                lineTaxable
                                  ? `$${lineTaxAmount.toFixed(2)}`
                                  : "Non-taxable"
                              }
                              readOnly
                            />
                          </div>
                        )}
                        <div className="col-lg-4">
                          <label className="table-cells">Product Location</label>
                          <input
                            type="text"
                            className="form-control"
                            value={(item.product_current_status || item.current_status || "").replace(/_/g, " ")}
                            disabled
                            readOnly
                          />
                        </div>
                        <div className="col-lg-7">
                          <label className="table-cells">Bin Location</label>
                          <input
                            type="text"
                            className="form-control text-ellipsis"
                            value={
                              item.location && item.location.length > 0
                                ? `${item.location[0].warehouse_name || ""} > ${item.location[0].zone_name || ""} > ${item.location[0].rack_name || ""} > ${item.location[0].bin_name || ""}`
                                : "No location assigned"
                            }
                            disabled
                            readOnly
                            title={
                              item.location && item.location.length > 0
                                ? `${item.location[0].warehouse_name || ""} > ${item.location[0].zone_name || ""} > ${item.location[0].rack_name || ""} > ${item.location[0].bin_name || ""}`
                                : "No location details available"
                            }
                          />
                        </div>
                        {(() => {
                          const rowData = resolveSelectedLineItemForDisplay(item, selectedProducts);
                          const custBlock = getCustomizationsBlockFromLineItem(rowData);
                          const custRows = getVisibleCustomizationFieldsForDisplay(custBlock);
                          if (custRows.length === 0) return null;
                          return (
                            <div className="col-lg-12 mt-2">
                              <div className="fw-medium table-cells small mb-2 mt-1">customizations:</div>
                              <div className="row g-2 g-lg-3">
                                {custRows.map((field, idx) => (
                                  <div
                                    className="col-12 col-sm-6 col-lg-6"
                                    key={`${String(rowData.product_id)}-${String(rowData.slot_id ?? "")}-cust-${String(field.field_name || idx)}`}
                                  >
                                    <div className="px-3 py-2 small text-break">
                                      <span className="fs-14 fw-medium">{field.field_name || "Field"}</span>
                                      <span className="text-muted"> : </span>
                                      {(() => {
                                        const v = String(field.value ?? "").trim();
                                        const ft = String(field.field_type || "").toLowerCase();
                                        const isFile = ft === "file" || ft === "image";
                                        const m = (customizationImageUrlMap as Record<string, string>) || {};
                                        const src =
                                          isFile && /^[a-f0-9]{24}$/i.test(v)
                                            ? m[v] || getImageSrc(v, m) || ""
                                            : "";
                                        if (src) {
                                          return (
                                            <img
                                              src={src}
                                              alt=""
                                              style={{
                                                maxHeight: 120,
                                                maxWidth: "100%",
                                                borderRadius: 8,
                                                border: "1px solid #dee2e6",
                                                display: "block",
                                                marginTop: 4,
                                              }}
                                            />
                                          );
                                        }
                                        return <span className="fs-14">{String(field.value ?? "")}</span>;
                                      })()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    );
                    };
                    return (
                      <>
                        {inventoryProducts.length > 0 && (
                          <>
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <span className="fw-semibold table-cells">Inventory Products</span>
                            </div>
                            {inventoryProducts.map((item, i) => renderCard(item, i + 1, false))}
                          </>
                        )}
                        {purchasedProducts.length > 0 && (
                          <>
                            {inventoryProducts.length > 0 && <hr className="my-4" />}
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <span className="fw-semibold table-cells">Purchased Products</span>
                            </div>
                            {purchasedProducts.map((item, i) =>
                              renderCard(item, inventoryProducts.length + i + 1, true)
                            )}
                          </>
                        )}
                      </>
                    );
                  })()
                )}
              </div>
            </div>
            {pickerOpen && pickerUserType && pickerUserId && token ? (
              <RealtorProductPicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                userType={pickerUserType}
                userId={pickerUserId}
                signageCompanyId={pickerSignageCompanyId}
                token={token}
                onAdd={handleAddFromPicker}
              />
            ) : null}

            <div className="row col-lg-12 mb-3">
              <div className="col-lg-6 mb-3 mb-lg-0 pe-0">
                <InstructionsCreateCard
                  title="Instructions"
                  mode="instructions_files"
                  loginType="signage"
                  value={formik.values.instructions}
                  onChange={(v) => formik.setFieldValue("instructions", v, false)}
                  onLocalInstructionFilesChange={handlePendingInstructionFilesChange}
                  placeholder="Add instructions here"
                  rows={4}
                  className="mt-3"
                  iconUrl={OrderNotes}
                  iconColor="#10B981"
                  iconBackground="#EAF7F1"
                />
              </div>
              <div className="align-items-center card-body col-lg-5 justify-content-end ms-0 ms-2 ms-lg-0 ms-lg-5 offset-1 p-4 shadow-lg">
                <div className="align-items-center d-flex mb-3">
                  <span
                    aria-hidden="true"
                    className="image-icons"
                    style={{
                      backgroundColor: "#00a63e",
                      WebkitMaskImage: `url(${Doller})`,
                      maskImage: `url(${Doller})`,
                    }}
                  />
                  <SectionHeader title="Cost Breakdown" icon="" color="" size="lg" divider />
                </div>
               {selectedInstaller?.value && (
              <>
                <OrderDiscountCodeField
                  className="mb-2"
                  value={couponInput}
                  appliedCode={discountDisplay.code}
                  discountAmount={discountDisplay.amount}
                  voucherApplied={
                    discountDisplay.amount > 0 && Boolean(discountDisplay.code.trim())
                  }
                  disabled={isRecomputingTax}
                  error={couponResolveError}
                  onChange={setCouponInput}
                  onApply={(code) => {
                    void applyDiscountCode(code);
                  }}
                  onClear={() => {
                    void clearDiscountCode();
                  }}
                />
               </>
               )}

                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="fw-medium">Units Purchased:</span>
                  <span className="fw-semibold">{newCatalogUnitsPurchased}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="fw-medium">Total Units:</span>
                  <span className="fw-semibold">{totalUnits}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="fw-medium">Installation Cost:</span>
                  <span className="fw-semibold">${resolvedInstallCost.toFixed(2)}</span>
                </div>
                {resolvedAdditionalCost > 0 && (
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="fw-medium">Additional Cost:</span>
                    <span className="fw-semibold">${resolvedAdditionalCost.toFixed(2)}</span>
                  </div>
                )}
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="fw-medium">Units Cost:</span>
                  <span className="fw-semibold">${Number(unitsCost || 0).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="fw-medium">Sub-total:</span>
                  <span className="fw-semibold">${resolvedSubtotalAmount.toFixed(2)}</span>
                </div>
                {discountDisplay.code && discountDisplay.amount > 0 && (
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="fw-medium">
                      Discount:
                      {discountDisplay.code ? (
                        <span style={{ color: "#00a63e" }}> ({discountDisplay.code})</span>
                      ) : null}
                    </span>
                    <span className="fw-semibold" style={{ color: "#00a63e" }}>
                      -${discountDisplay.amount.toFixed(2)}
                    </span>
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center py-2">
                  {(() => {
                    const { labelText, tooltip } = buildSpecialFeeCostBreakdownDisplay(selectedSpecialFees);
                    return (
                      <>
                        <span className="fw-medium me-2" style={{ minWidth: 0 }} title={tooltip}>
                          {labelText}:
                        </span>
                        <span className="fw-semibold flex-shrink-0">
                          ${Number(formik.values.totals?.specialfee_subtotal || 0).toFixed(2)}
                        </span>
                      </>
                    );
                  })()}
                </div>

                <div className="py-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      type="button"
                      onClick={() => setShowTaxDetails(v => !v)}
                      className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                      aria-expanded={showTaxDetails}
                      aria-controls="tax-breakdown"
                    >
                      {/* <i className={`bi ${showTaxDetails ? "bi-chevron-down" : "bi-chevron-right"} me-1`} /> */}
                      <span className="order-card-title">Tax :</span>
                    </button>
                    <span className="order-card-cells">
                      <span>
                        ${Number(formik.values.tax || 0).toFixed(2)}
                      </span>
                    </span>
                  </div>

                  {/* {showTaxDetails && (
                    <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
                      <div className="d-flex justify-content-between small mb-1">
                        <span>Installation Tax</span>
                        <span className="order-card-cells">
                          ${Number(formik.values.totals.installation_tax_amount).toFixed(2)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <span>Special Fee Tax</span>
                        <span className="order-card-cells">
                          ${Number(formik.values.totals.specialfee_tax_amount).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )} */}
                </div>

                <hr className="my-3" />
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="fw-bold fs-5">Total Amount:</span>
                  <span className="fw-bold fs-5 text-primary">
                    ${Number(formik.values?.totals?.grand_total || 0).toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="fw-medium">Already Paid:</span>
                  <span className="fw-semibold">${Number(formik.values?.already_paid ?? 0).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="fw-bold">Remaining Balance:</span>
                  <span className="fw-bold text-success">
                    ${Math.max(
                      0,
                      Number(formik.values?.totals?.grand_total || 0) -
                        Number(formik.values?.already_paid ?? 0)
                    ).toFixed(2)}
                  </span>
                </div>

              </div>
            </div>

            <div className="row mb-3 col-lg-12 rcust-margin-4 mt-4">
              <div className="col-lg-12 d-block d-lg-flex justify-content-between">
                <div className="col-lg-2">
                  <button
                    type="button"
                    onClick={() => safeNavigate(-1)}
                    className="btn back-btn surface-btn fw-bold fs-16 w-100"
                  >
                    <i className="bi bi-chevron-left me-2" />
                    Back
                  </button>
                </div>

                <div className="d-block d-lg-flex align-items-baseline justify-content-end">
                  <div className="col-lg-10 mt-3">
                    <div className="d-flex d-lg-block form-check justify-content-center mb-3 mb-lg-0">
                      <input
                        type="checkbox"
                        id="localLaws"
                        name="localLawsChecked"
                        className="form-check-input"
                        checked={formik.values.localLawsChecked}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <label htmlFor="localLaws" className="form-check-label ms-2">
                        I acknowledge to local laws
                      </label>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary save-btn surface-btn fw-bold fs-16 w-100"
                    disabled={formSubmitting || isRecomputingTax || !formik.values.localLawsChecked}
                  >
                    {isRecomputingTax ? "Calculating..." : "Save Draft"}
                  </button>
                </div>
              </div>
            </div>
          </div>
           {selectedInstaller?.value &&  (
          <div className="col-lg-4">
            <PONumber
              mode="create"
              disabled={Boolean(String(new URLSearchParams(location.search).get("project_id") || "").trim())}
              className="mb-3"
              value={String(formik.values.po_number || "")}
              onChange={(v) => formik.setFieldValue("po_number", v)}
            />
            <SpecialFeeui
              token={token || ""}
              personId={userId || ""}  // ✅ Use parent firm ID for BROKERAGE_AGENT
              supplierId={selectedInstaller?.value || ""}
              formik={formik}
              name="specialfee"
              apiBase={process.env.REACT_APP_API_URL || ""}
              readOnly={false}
              disabled={isRecomputingTax}
              title="Special Fees"
              onOptionsLoaded={(allOptions) => {
                // ✅ Receive all available fees (including required field) from component
                setAllAvailableSpecialFees(allOptions);
                console.log("All available special fees from component:", allOptions);
              }}
              onUserChange={(nextFees) => {
                if (isStudioRestoreApplyingRef.current) {
                  return;
                }
                if (isRecomputingRef.current) {
                  safeConsole.log("⏸️ Recompute in progress, skipping special fee change");
                  return;
                }
                isUpdatingFromRecomputeRef.current = false;
                setSelectedSpecialFees(nextFees);
                formik.setFieldValue("specialfee", nextFees, false);
              }}
            />
            {/* <div className="col-lg-12 mb-3 rounded-3 shadow-sm overflow-hidden min-h-50 card-body shadow-lg">
              <div className="bg-primary-subtle d-flex align-items-center justify-content-center gap-2 py-3">
                <SectionHeader title="Payments" icon="ri-bank-card-line" color="#10b981" size="md" divider />
              </div>
            </div>

            <div className="col-lg-12 rounded-3 shadow-sm overflow-hidden min-h-50 card-body shadow-lg mb-3">
              <div className="bg-secondary-subtle d-flex align-items-center justify-content-center gap-2 py-3">
                <SectionHeader title="Installation Log" icon="ri-history-line" color="#d63a95" size="md" divider />
              </div>
            </div>
          </div> */}
          </div>
           )}
        </div>
        {showAddressForm && (
          <AddressFormModal
            isOpen={showAddressForm}
            onClose={() => {
              setShowAddressForm(false);
            }}
            token={token || ""}
            owner={{
              value: userId || "",
              label: userName || "",
            }}
            useType="billing"
            userType="agent"
            prefill={{
              // Prefill from your existing parent formik values (same field names you used)
              label_of_address: formik.values.label_of_address || "",
              firstName: formik.values.firstName || "",
              lastName: formik.values.lastName || "",
              company: formik.values.company || "",
              phone: formik.values.phone || "",
              addressLine1: formik.values.addressLine1 || "",
              addressLine2: formik.values.addressLine2 || "",
              city: formik.values.city || "",
              state: formik.values.state || "",
              zipcode: formik.values.zipcode || "",
              country: formik.values.country || "",
            }}
            onCreated={(createdRaw, option, useType) => {
              setAddressObjects((prev) => [createdRaw, ...(prev || [])]);
              formik.setFieldValue(
                useType === "billing" ? "billingAddressId" : "shippingAddressId",
                option.value
              );
              const isOffice = !!selectedAgent;

              if (useType === "billing") {
                if (isOffice) {

                } else {
                  setSelectedBillingAddress(createdRaw?.address_details || null);
                }
              } else {
                if (isOffice) {

                } else {

                }
              }

              // 4) Mirror to shipping if same-as-billing is checked
              if (useType === "billing" && formik.values.isSameAsBilling) {
                formik.setFieldValue("shippingAddressId", option.value);
                if (isOffice) {
                  setInstallationAddress(createdRaw);
                } else {
                  setInstallationAddress(createdRaw?.address_details || null);
                }
              }
            }}
            afterCreateRefresh={() => {
              // Refresh the addresses list
              if (userId) {
                const fetchAddresses = async () => {
                  try {
                    const response = await fetch(
                      `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
                      {
                        method: "GET",
                        headers: {
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "application/json",
                        },
                      }
                    );
                    const data = await response.json();
                    setAddressObjects(data);
                  } catch (err) {
                    safeConsole.error("Error fetching addresses:", err);
                    await handleApiError(err, {
                      module: "installation",
                      action: "Agent_Add_Installation_fetch_addresses_refresh",
                      userId: userId || undefined,
                      licenseType: licenseType || undefined,
                      userName: userName || undefined,
                      url: `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
                      method: "GET",
                    }, token);
                  }
                };
                fetchAddresses();
              }
            }}
          />
        )}
      </form>
      {formSubmitting && (
        <div className="mt-3">
          <div className="progress">
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${uploadProgress}%` }}
            >
              {uploadProgress}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentAddInstallation;
